import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { StatusBadge } from '../components/StatusBadge';
import { DeliveryTabs } from '../components/DeliveryTabs';
import { ClockTimePicker } from '../components/ClockTimePicker';
import { customerOfferPricing, feeLabel } from '../utils/offerPricing';
import StripeCheckoutDialog from '../components/StripeCheckoutDialog';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import api, { createDelivery, uploadDeliveryPhotos, getMyDeliveries, getOffers, createPaymentCheckout, getPaymentStatus, acceptAndPay, getCounties, submitReview, getDeliveryReview, downloadVatInvoiceUrl, editDelivery, cancelDelivery, markDeliveryMessagesRead, quickPostClaim, resolveAssetUrl, getMessagesUnreadCount } from '../services/api';
import AddressAutocomplete from '../components/AddressAutocomplete';
import RouteMap from '../components/RouteMap';
import DeliveryTimeBadge from '../components/DeliveryTimeBadge';
import DeliveryDetailDialog from '../components/DeliveryDetailDialog';
import MessagesInbox from '../components/MessagesInbox';
import { ContactCard } from '../components/ContactCard';
import { containsPhoneNumber, containsContactInfo, validateDescription, PHONE_BLOCK_MSG_DESC, CONTACT_BLOCK_MSG_DESC, DESCRIPTION_HELPER_TEXT, DESCRIPTION_MIN, DESCRIPTION_MAX } from '../lib/textSafety';
import { PremiumBadge } from '../components/PremiumBadge';
import PaymentMethodBadges from '../components/PaymentMethodBadges';
import CancelBookingButton from '../components/CancelBookingButton';
import { HomeHero, HowItWorks, FindDriverCard, WhyJustDeliver } from '../components/customer/CustomerHomeSections';
import { PostDeliveryHeader, ItemTypeTiles } from '../components/customer/PostDeliveryUI';
import PaymentMethodChoiceDialog from '../components/PaymentMethodChoiceDialog';
import EmailPreferencesPanel from '../components/EmailPreferencesPanel';
import QuickReview from '../components/QuickReview';
import useAutoRefresh from '../hooks/useAutoRefresh';
import LastUpdatedIndicator from '../components/LastUpdatedIndicator';
import SupportChat from '../components/SupportChat';
import CustomerNav from '../components/CustomerNav';
import ExpiryCountdown from '../components/ExpiryCountdown';
import ExpiryCardPremium from '../components/ExpiryCardPremium';
import AskAQuestion from '../components/AskAQuestion';
import { NotificationPrompt, NotificationToggle } from '../components/NotificationPrompt';
import { SongPlayer } from '../components/SongPlayer';
import DashboardFooter from '../components/DashboardFooter';
import { DIRECTORY_ENABLED } from '../config/features';
import { toast } from 'sonner';
import { Package, MapPin, Clock, Plus, Eye, FileText, CreditCard, CheckCircle, AlertCircle, Shield, Navigation, Camera, Ruler, Weight, X, ChevronDown, Lock, HelpCircle, ChevronUp, KeyRound, Copy, User, Users, Phone, Star, Truck, Euro, Search, Download, Pencil, Zap, Banknote, Hash } from 'lucide-react';

const ITEM_TYPES = [
  { value: 'package', label: 'Parcel / Box', emoji: '📦' },
  { value: 'envelope', label: 'Documents', emoji: '✉️' },
  { value: 'furniture', label: 'Furniture', emoji: '🛋️' },
  { value: 'sofa', label: 'Sofa / Bed', emoji: '🛏️' },
  { value: 'appliance', label: 'Appliance', emoji: '🧺' },
  { value: 'electronics', label: 'Electronics', emoji: '💻' },
  { value: 'pallet', label: 'Pallet / Business', emoji: '🏪' },
  { value: 'house_move', label: 'House Move', emoji: '🏠' },
  { value: 'other', label: 'Other', emoji: '➕' },
];

// Pill-style 2-option choice used in the lifting/loading section. Captures
// the value when the user clicks; styled like the offer-tag chips so the
// form has visual consistency.
function HelpChoice({ label, value, onChange, options, dataTestId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-gray-700">{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={dataTestId}>
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(selected ? '' : opt.value)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors flex-1 sm:flex-initial justify-center sm:justify-start min-w-0 ${selected ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}
              data-testid={`${dataTestId}-${opt.value}`}
            >
              <span className="text-base flex-shrink-0">{opt.emoji}</span>
              <span className="text-left">{opt.text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState(() => {
    const v = searchParams.get('view');
    return ['home', 'post', 'deliveries', 'messages', 'settings'].includes(v) ? v : 'home';
  });

  // Keep activeView in sync with the URL after mount (e.g. when the navbar
  // dropdown navigates to ?view=settings while this component is already
  // mounted). Without this the useState initializer only fires once.
  useEffect(() => {
    const v = searchParams.get('view');
    const allowed = ['home', 'post', 'deliveries', 'messages', 'settings'];
    const next = allowed.includes(v) ? v : 'home';
    setActiveView((prev) => (prev === next ? prev : next));
  }, [searchParams]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState(1);
  const [custDeliveryPage, setCustDeliveryPage] = useState(1);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersDialogOpen, setOffersDialogOpen] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [counties, setCounties] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [formData, setFormData] = useState({
    pickup_location: '', pickup_county: '', pickup_area: '', pickup_eircode: '',
    pickup_lat: null, pickup_lng: null,
    dropoff_location: '', dropoff_county: '', dropoff_area: '', dropoff_eircode: '',
    dropoff_lat: null, dropoff_lng: null,
    receiver_name: '', receiver_phone: '', receiver_email: '',
    item_description: '', item_type: '', preferred_time: '',
    dimensions_length: '', dimensions_width: '', dimensions_height: '', weight_kg: '',
    collection_help: '', delivery_help: '', driver_assistance: '',
    customer_budget: '',
    distance_km: null, duration_min: null, route_geometry: null,
  });
  // Multi-drop addresses (business customers only)
  const [additionalDrops, setAdditionalDrops] = useState([]);
  const [deliveryTimeType, setDeliveryTimeType] = useState('before');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTimeBefore, setDeliveryTimeBefore] = useState('');
  const [deliveryTimeFrom, setDeliveryTimeFrom] = useState('');
  const [deliveryTimeTo, setDeliveryTimeTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Post-a-delivery wizard: each step is validated before the customer can
  // move on, and the form always opens on step 1.
  useEffect(() => { if (showForm) { setStep(1); } }, [showForm]);
  const stepDone = [
    !!(formData.pickup_location && formData.pickup_county && (formData.pickup_area || '').trim()),
    !!(formData.dropoff_location && formData.dropoff_county && (formData.dropoff_area || '').trim()),
    !!formData.item_type && !validateDescription(formData.item_description),
    !!deliveryDate && (deliveryTimeType !== 'before' || !!deliveryTimeBefore) && (deliveryTimeType !== 'range' || (!!deliveryTimeFrom && !!deliveryTimeTo)),
    false,
  ];
  const STEP_ERRORS = [
    'Add the pickup address, county and town / city / area to continue.',
    'Add the drop-off address, county and town / city / area to continue.',
    'Choose what you are delivering and add a description (min 10 characters).',
    'Pick a delivery date and time to continue.',
  ];
  const goNextStep = () => {
    if (!stepDone[step - 1]) {
      toast.error(STEP_ERRORS[step - 1]);
      return;
    }
    setStep(s => Math.min(5, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goPrevStep = () => {
    setStep(s => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // After a successful POST /api/deliveries, we show a friendly confirmation
  // modal (green checkmark + helpful copy + dual CTAs) instead of just a
  // toast. successDelivery holds the freshly-created delivery row so the
  // "View My Job" button can deep-link straight to its detail dialog.
  const [successDelivery, setSuccessDelivery] = useState(null);
  // Messages-tab unread count — drives the nav badge. Polled by the inbox
  // component while it's mounted; we also poll lightly here every 30 s so the
  // badge stays fresh even when the user is on other tabs.
  const [messagesUnread, setMessagesUnread] = useState(0);

  const [showInstructions, setShowInstructions] = useState(false);

  // Delivery detail dialog state — opens when a row in History/Recent/Archive is tapped
  const [detailDelivery, setDetailDelivery] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleSelectJob = async (job) => {
    // We have a compact job from the tabs view — fetch the full delivery doc
    // (includes addresses, timestamps, capture_status, etc.) before opening.
    setDetailOpen(true);
    setDetailDelivery(job); // show shell immediately
    try {
      const { data } = await api.get(`/deliveries/${job.id}`);
      setDetailDelivery(data);
    } catch (e) {
      // Keep the partial view; user can still see basic info
    }
  };

  // Review state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewDelivery, setReviewDelivery] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewedDeliveries, setReviewedDeliveries] = useState({});

  const fetchDeliveries = useCallback(async () => {
    try {
      const response = await getMyDeliveries();
      setDeliveries(response.data);
      // Hydrate the reviewed-map from the server so QuickReview doesn't
      // re-render after a page reload for deliveries the customer has
      // already rated. Merged (not replaced) so any in-session submissions
      // that haven't been round-tripped yet aren't lost.
      const serverReviewed = {};
      for (const d of response.data || []) {
        if (d.customer_review && d.id) {
          serverReviewed[d.id] = d.customer_review;
        }
      }
      if (Object.keys(serverReviewed).length) {
        setReviewedDeliveries((prev) => ({ ...serverReviewed, ...prev }));
      }
    } catch (error) {
      toast.error('Failed to fetch deliveries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getCounties().then(res => setCounties(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (paymentStatus === 'success' && sessionId) {
      const pollPayment = async () => {
        try {
          const response = await getPaymentStatus(sessionId);
          if (response.data.status === 'paid') {
            toast.success('Payment successful! Your delivery is confirmed.');
            fetchDeliveries();
          } else if (response.data.status === 'pending') {
            setTimeout(pollPayment, 2000);
          }
        } catch (error) {
          console.error('Payment status check failed:', error);
        }
      };
      pollPayment();
      setSearchParams({});
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled. You can try again.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, fetchDeliveries]);

  useEffect(() => { fetchDeliveries(); }, [fetchDeliveries]);

  // 60-second background refresh — deliveries + offers + status changes.
  // Auto-pauses while the tab is hidden. The state update flows into the
  // existing render tree, so any open dialogs and scroll positions are
  // preserved (no unmount).
  const { lastRefreshedAt, isRefreshing } = useAutoRefresh(fetchDeliveries, 60_000);

  // Poll the Messages unread count every 30 s so the nav badge stays fresh
  // even when the user is on other tabs. The inbox itself also reports its
  // own unread count on every load + after each open/send for snappier UX,
  // but this background poll is the safety net for "got a message while
  // browsing jobs" scenarios.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await getMessagesUnreadCount();
        if (alive) setMessagesUnread(data?.unread_conversations || 0);
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ── Quick Post claim handoff ────────────────────────────────────────────
  // After a guest signs in/up they land back here with ?claim=TOKEN&offer=ID.
  // We link the guest delivery to their account, then open the bid list so
  // they can complete the accept-and-pay flow.
  useEffect(() => {
    const claimToken = searchParams.get('claim');
    const targetOfferId = searchParams.get('offer');
    if (!claimToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await quickPostClaim(claimToken);
        if (cancelled) return;
        toast.success('Your delivery is now linked to your account.');
        await fetchDeliveries();
        // Stash the offer id for the next render — the bids dialog opens
        // automatically once the delivery is in `deliveries`.
        if (targetOfferId) {
          window.__qpPendingOffer = { deliveryId: data.id, offerId: targetOfferId };
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Could not link your delivery.');
      } finally {
        // Claim has been attempted — drop the persisted redirect crumb that
        // RegisterPage left (VerifyEmailPage and LoginPage just *peek* at it
        // so it survives a /login bounce). This is the final consumer.
        try { localStorage.removeItem('postSignupRedirect'); } catch (e) { /* storage disabled */ }
        if (!cancelled) setSearchParams({});
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open offers dialog after a successful Quick Post claim.
  useEffect(() => {
    const pending = window.__qpPendingOffer;
    if (!pending || !deliveries?.length) return;
    const match = deliveries.find((d) => d.id === pending.deliveryId);
    if (!match) return;
    setSelectedDelivery(match);
    getOffers(match.id).then((res) => {
      setOffers(res.data || []);
      setOffersDialogOpen(true);
    }).catch(() => {});
    delete window.__qpPendingOffer;
  }, [deliveries]);

  // Deep link from emails (bid notifications, admin nudge):
  // /customer?view=deliveries&delivery=<id> → scroll to that delivery and
  // open its bids dialog straight away.
  const [deeplinkDone, setDeeplinkDone] = useState(false);
  useEffect(() => {
    const targetId = searchParams.get('delivery');
    if (!targetId || deeplinkDone || !deliveries?.length) return;
    const match = deliveries.find((d) => d.id === targetId);
    if (!match) return;
    setDeeplinkDone(true);
    setSelectedDelivery(match);
    setOffersDialogOpen(true);
    setOffersLoading(true);
    getOffers(match.id)
      .then((res) => setOffers(res.data || []))
      .catch(() => {})
      .finally(() => setOffersLoading(false));
    setTimeout(() => {
      document.querySelector(`[data-testid="delivery-card-${targetId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveries, searchParams, deeplinkDone]);


  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5 - photoFiles.length);
    setPhotoFiles(prev => [...prev, ...files]);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreviews(prev => [...prev, reader.result]);
      reader.readAsDataURL(f);
    });
  };

  const removePhoto = (idx) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== idx));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const descError = validateDescription(formData.item_description);
    if (descError) {
      toast.error(descError);
      return;
    }
    // Per-drop description guard — same rules as the main description, but
    // a drop description may be blank (no minimum length).
    if (user?.account_type === 'business') {
      for (let i = 0; i < additionalDrops.length; i++) {
        const d = additionalDrops[i];
        const dropDesc = (typeof d === 'string' ? '' : (d?.item_description || '')).trim();
        if (dropDesc && containsContactInfo(dropDesc)) {
          toast.error(`Drop ${i + 2}: contact details are not permitted in the description.`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      // Build preferred_time as JSON
      const to24h = (val) => {
        if (!val) return '';
        const [h, m, ap] = val.split(':');
        let hr = parseInt(h);
        if (ap === 'PM' && hr !== 12) hr += 12;
        if (ap === 'AM' && hr === 12) hr = 0;
        return `${String(hr).padStart(2, '0')}:${m}`;
      };
      const timeData = { type: deliveryTimeType, date: deliveryDate };
      if (deliveryTimeType === 'before') timeData.before = to24h(deliveryTimeBefore);
      else if (deliveryTimeType === 'range') { timeData.from = to24h(deliveryTimeFrom); timeData.to = to24h(deliveryTimeTo); }
      payload.preferred_time = JSON.stringify(timeData);
      // Convert numeric fields
      ['dimensions_length', 'dimensions_width', 'dimensions_height', 'weight_kg'].forEach(k => {
        payload[k] = payload[k] ? parseFloat(payload[k]) : null;
      });
      // customer_budget: keep as positive float, drop otherwise
      {
        const v = parseFloat(payload.customer_budget);
        payload.customer_budget = Number.isFinite(v) && v > 0 ? v : null;
      }
      // Trim the driver-facing town / city / area
      ['pickup_area', 'dropoff_area'].forEach(k => {
        payload[k] = (payload[k] || '').trim().slice(0, 30) || null;
      });
      // Remove empty optional strings
      ['pickup_county', 'pickup_eircode', 'dropoff_county', 'dropoff_eircode', 'item_type'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });
      // Multi-drop (business only) — strip empties, send legacy string list
      // for the older route map, full structured drops for new receiver-per-
      // drop tracking, and coord-rich waypoints for the route plotter.
      if (user?.account_type === 'business') {
        const drops = additionalDrops
          .map((d) => (typeof d === 'string' ? { address: d } : d))
          .filter((d) => (d?.address || '').trim());
        if (drops.length > 0) {
          payload.additional_drops = drops.map((d) => d.address.trim());
          payload.drops = drops.map((d) => ({
            address: d.address.trim(),
            county: d.county || null,
            eircode: d.eircode || null,
            lat: d.lat ?? null,
            lng: d.lng ?? null,
            receiver_name: (d.receiver_name || '').trim() || null,
            receiver_phone: (d.receiver_phone || '').trim() || null,
            receiver_email: (d.receiver_email || '').trim() || null,
            item_description: (d.item_description || '').trim() || null,
            // Per-drop dimensions (cm) and weight (kg). Blank → null so the
            // main delivery dimensions are used as the fallback at the stop.
            dimensions_length: d.dimensions_length ?? null,
            dimensions_width: d.dimensions_width ?? null,
            dimensions_height: d.dimensions_height ?? null,
            weight_kg: d.weight_kg ?? null,
            photos: Array.isArray(d.photos) ? d.photos.slice(0, 3) : [],
          }));
          const withCoords = drops.filter((d) => d.lat != null && d.lng != null);
          if (withCoords.length > 0) payload.waypoints = withCoords;
        }
      }

      const res = await createDelivery(payload);
      const newId = res.data.id;

      // Upload photos if any
      if (photoFiles.length > 0) {
        const fd = new FormData();
        photoFiles.forEach(f => fd.append('photos', f));
        await uploadDeliveryPhotos(newId, fd);
      }

      // Reset form state + close drawer. Then show the celebratory success
      // modal — the toast is replaced by a dedicated card with branded
      // copy + dual CTAs (see <Dialog open={!!successDelivery} …/> below).
      setFormData({ pickup_location: '', pickup_county: '', pickup_area: '', pickup_eircode: '', pickup_lat: null, pickup_lng: null, dropoff_location: '', dropoff_county: '', dropoff_area: '', dropoff_eircode: '', dropoff_lat: null, dropoff_lng: null, receiver_name: '', receiver_phone: '', receiver_email: '', item_description: '', item_type: '', preferred_time: '', dimensions_length: '', dimensions_width: '', dimensions_height: '', weight_kg: '', collection_help: '', delivery_help: '', driver_assistance: '', customer_budget: '', distance_km: null, duration_min: null, route_geometry: null });
      setAdditionalDrops([]);
      setDeliveryTimeType('before'); setDeliveryDate(''); setDeliveryTimeBefore(''); setDeliveryTimeFrom(''); setDeliveryTimeTo('');
      setPhotoFiles([]);
      setPhotoPreviews([]);
      setShowForm(false);
      fetchDeliveries();
      setSuccessDelivery({ ...(res.data || {}), id: newId });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create delivery request');
    } finally {
      setSubmitting(false);
    }
  };

  const viewOffers = async (delivery) => {
    setSelectedDelivery(delivery);
    setOffersDialogOpen(true);
    setOffersLoading(true);
    try {
      const response = await getOffers(delivery.id);
      setOffers(response.data);
      // Clear the customer's unread-message badge for this delivery once
      // they actually see the thread — fire-and-forget so the modal doesn't
      // wait on it.
      if ((delivery.unread_message_count || 0) > 0) {
        markDeliveryMessagesRead(delivery.id)
          .then(() => fetchDeliveries())
          .catch(() => {});
      }
    } catch (error) {
      toast.error('Failed to fetch offers');
    } finally {
      setOffersLoading(false);
    }
  };

  // 30-second refresh while the customer is on the offers dialog — faster
  // than the main polling loop because bids arrive live. Only enabled when
  // the dialog is open and a delivery is selected, so we don't hammer the
  // API in the background.
  const refreshOffersOnly = useCallback(async () => {
    if (!selectedDelivery) return;
    try {
      const response = await getOffers(selectedDelivery.id);
      setOffers(response.data);
    } catch (_e) { /* silent — polling should not spam toasts */ }
  }, [selectedDelivery]);
  useAutoRefresh(refreshOffersOnly, 30_000, offersDialogOpen && !!selectedDelivery);

  const handleAcceptAndPay = async (offer, paymentMethod = 'direct') => {
    if (!selectedDelivery) return;
    if (paymentMethod === 'direct' && !offer.driver_accepts_direct) {
      toast.error('This driver does not accept direct payment.');
      return;
    }
    setProcessingPayment(true);
    try {
      await acceptAndPay(selectedDelivery.id, offer.id, paymentMethod);
      const originUrl = window.location.origin;
      const response = await createPaymentCheckout(selectedDelivery.id, originUrl);
      const secret = response.data.client_secret;
      if (secret) {
        setOffersDialogOpen(false);
        setCheckoutSecret(secret);
      } else {
        // Safety net if Stripe ever returns a hosted-only session.
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      setProcessingPayment(false);
    }
  };

  // In-app Stripe payment form (Embedded Checkout).
  const [checkoutSecret, setCheckoutSecret] = useState(null);
  const closeCheckout = () => {
    setCheckoutSecret(null);
    setProcessingPayment(false);
    fetchDeliveries();
  };

  // PR 2 — Payment Method choice dialog (only when driver supports both).
  const [paymentChoiceOffer, setPaymentChoiceOffer] = useState(null);
  const openPaymentChoice = (offer) => setPaymentChoiceOffer(offer);
  const closePaymentChoice = () => setPaymentChoiceOffer(null);
  const submitPaymentChoice = () => {
    const offer = paymentChoiceOffer;
    closePaymentChoice();
    if (offer) handleAcceptAndPay(offer, 'direct');
  };

  // Pay Driver Directly: one route only — always show the breakdown first.
  const handleOfferCTA = (offer) => openPaymentChoice(offer);

  const formatDate = (dateString) => {
    try {
      const parsed = JSON.parse(dateString);
      const d = new Date(parsed.date + 'T00:00:00');
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const fmtTime = (t) => { const [h,m] = t.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr-12 : hr || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`; };
      if (parsed.type === 'before') return `${dateStr} by ${fmtTime(parsed.before)}`;
      if (parsed.type === 'range') return `${dateStr}, ${fmtTime(parsed.from)} – ${fmtTime(parsed.to)}`;
      return `${dateStr}, Flexible`;
    } catch {
      return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const openReviewDialog = async (delivery) => {
    setReviewDelivery(delivery);
    setReviewRating(5);
    setReviewComment('');
    // Check if already reviewed
    try {
      const res = await getDeliveryReview(delivery.id);
      if (res.data.review) {
        setReviewedDeliveries(prev => ({ ...prev, [delivery.id]: res.data.review }));
        return; // Don't open dialog if already reviewed
      }
    } catch {}
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewDelivery) return;
    setReviewSubmitting(true);
    try {
      await submitReview(reviewDelivery.id, { rating: reviewRating, comment: reviewComment });
      toast.success('Review submitted! Thank you.');
      setReviewedDeliveries(prev => ({ ...prev, [reviewDelivery.id]: { rating: reviewRating, comment: reviewComment } }));
      setReviewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  // ============ Edit / Cancel Job ============
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const parsePreferredTime = (raw) => {
    // Returns { type, date, before, from, to } in 24h "HH:MM" format
    if (!raw) return { type: 'before', date: '', before: '', from: '', to: '' };
    try {
      const p = JSON.parse(raw);
      return {
        type: p.type || 'before',
        date: p.date || '',
        before: p.before || '',
        from: p.from || '',
        to: p.to || '',
      };
    } catch {
      return { type: 'before', date: '', before: '', from: '', to: '' };
    }
  };

  const to12h = (t) => {
    // Convert "HH:MM" 24h → "h:MM:AP" 12h (format the ClockTimePicker expects)
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    const ap = hr >= 12 ? 'PM' : 'AM';
    const hh = hr % 12 === 0 ? 12 : hr % 12;
    return `${hh}:${m}:${ap}`;
  };

  const to24h = (val) => {
    if (!val) return '';
    const parts = val.split(/[: ]/);
    if (parts.length < 3) return val;
    const [h, m, ap] = parts;
    let hr = parseInt(h);
    if (ap === 'PM' && hr !== 12) hr += 12;
    if (ap === 'AM' && hr === 12) hr = 0;
    return `${String(hr).padStart(2, '0')}:${m}`;
  };

  const startEditJob = (delivery) => {
    const pt = parsePreferredTime(delivery.preferred_time);
    setEditingDelivery(delivery);
    setEditForm({
      item_description: delivery.item_description || '',
      item_type: delivery.item_type || '',
      dimensions_length: delivery.dimensions_length || '',
      dimensions_width: delivery.dimensions_width || '',
      dimensions_height: delivery.dimensions_height || '',
      weight_kg: delivery.weight_kg || '',
      collection_help: delivery.collection_help || '',
      delivery_help: delivery.delivery_help || '',
      driver_assistance: delivery.driver_assistance || '',
      customer_budget: delivery.customer_budget != null ? String(delivery.customer_budget) : '',
      time_type: pt.type,
      time_date: pt.date,
      time_before: to12h(pt.before),
      time_from: to12h(pt.from),
      time_to: to12h(pt.to),
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDelivery || !editForm) return;
    const descError = validateDescription(editForm.item_description);
    if (descError) {
      toast.error(descError);
      return;
    }
    setEditSubmitting(true);
    try {
      const td = { type: editForm.time_type, date: editForm.time_date };
      if (editForm.time_type === 'before') td.before = to24h(editForm.time_before);
      else if (editForm.time_type === 'range') { td.from = to24h(editForm.time_from); td.to = to24h(editForm.time_to); }

      // Send form values as-is. Empty strings/empty numerics → undefined so
      // axios drops the key (preserve existing DB value); explicitly set values
      // (including the chosen help options) are sent through untouched.
      const pickStr = (v) => (v !== '' && v !== null && v !== undefined ? v : undefined);
      const pickNum = (v) => (v !== '' && v !== null && v !== undefined ? parseFloat(v) : undefined);

      const patch = {
        item_description: pickStr(editForm.item_description),
        item_type: pickStr(editForm.item_type),
        dimensions_length: pickNum(editForm.dimensions_length),
        dimensions_width: pickNum(editForm.dimensions_width),
        dimensions_height: pickNum(editForm.dimensions_height),
        weight_kg: pickNum(editForm.weight_kg),
        collection_help: pickStr(editForm.collection_help),
        delivery_help: pickStr(editForm.delivery_help),
        driver_assistance: pickStr(editForm.driver_assistance),
        // customer_budget: send explicitly (positive → number, empty → null
        // to clear a previously-entered value). We deliberately don't use
        // pickNum here because we WANT to be able to clear the budget.
        customer_budget: (
          editForm.customer_budget === '' || editForm.customer_budget === null || editForm.customer_budget === undefined
            ? null
            : (Number.isFinite(parseFloat(editForm.customer_budget)) && parseFloat(editForm.customer_budget) > 0
                ? parseFloat(editForm.customer_budget)
                : null)
        ),
        preferred_time: JSON.stringify(td),
      };

      await editDelivery(editingDelivery.id, patch);
      toast.success('Delivery updated');
      setEditDialogOpen(false);
      setEditingDelivery(null);
      setEditForm(null);
      fetchDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCancelJob = (delivery) => {
    if (!delivery) return;
    setCancelTarget(delivery);
    setCancelDialogOpen(true);
  };

  const CANCEL_REASONS = [
    { key: 'bids_too_expensive', label: 'Bids too expensive' },
    { key: 'found_alternative_driver', label: 'Found alternative driver' },
    { key: 'no_longer_needed', label: 'No longer need delivery' },
  ];

  const confirmCancelWithReason = async (reasonKey, reasonLabel) => {
    if (!cancelTarget) return;
    const delivery = cancelTarget;
    setCancellingId(delivery.id);
    try {
      await cancelDelivery(delivery.id, { reason: reasonKey, reason_label: reasonLabel });
      toast.success('Delivery cancelled');
      setCancelDialogOpen(false);
      setCancelTarget(null);
      fetchDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel delivery');
    } finally {
      setCancellingId(null);
    }
  };

  const StarRating = ({ rating, onRate, interactive = false, size = 'w-5 h-5' }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${size} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
          onClick={interactive ? () => onRate(star) : undefined}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24 md:pb-0" data-testid="customer-dashboard">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Animated delivery van — subtle brand motion at the top of the dashboard */}
        <div className="overflow-hidden h-8 relative mb-4" data-testid="customer-animated-van">
          <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-gray-200"></div>
          <div className="animated-van absolute" style={{ top: 0 }}>
            <Truck className="w-7 h-7 text-[#2E7D32]" strokeWidth={1.5} />
          </div>
        </div>

        {activeView === 'home' && <SongPlayer testId="customer-song-player" />}

        {/* Book with Confidence sits at the bottom of the page — see
            `trustBanner` rendered just before </main>. */}

        {/* Header */}
        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 ${activeView === 'post' ? 'hidden' : ''}`}>
          <div className={activeView === 'messages' ? 'hidden' : ''}>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-semibold font-['Inter'] text-gray-900">
                {user?.account_type === 'business' && user?.company_name ? user.company_name : (activeView === 'home' ? `Welcome back${user?.name ? `, ${String(user.name).split(' ')[0]}` : ''}` : 'My Deliveries')}
              </h1>
              {user?.account_type === 'business' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-bold uppercase tracking-wider rounded-full" data-testid="business-badge">
                  Business
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-1 flex items-center gap-2 flex-wrap">
              <span>{activeView === 'home' ? 'Post a job, compare offers, pick your driver' : 'Post jobs, compare offers, track in real-time'}</span>
              <LastUpdatedIndicator lastRefreshedAt={lastRefreshedAt} isRefreshing={isRefreshing} />
            </p>
          </div>
          <div className={`flex flex-col sm:flex-row gap-2 ${activeView === 'home' ? 'hidden' : ''}`}>
            <Button
              onClick={() => {
                setActiveView('post');
                setSearchParams({ view: 'post' });
                setShowForm(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-2 rounded-sm h-11 px-6 text-base font-semibold shadow-sm"
              data-testid="new-delivery-btn"
            >
              <Truck className="w-5 h-5" /> Post a Delivery Job
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/quick-post')}
              variant="outline"
              className="bg-white border-2 border-amber-300 text-amber-900 hover:bg-amber-50 hover:border-amber-500 gap-2 rounded-sm h-11 px-5 text-base font-semibold"
              data-testid="customer-quick-post-btn"
            >
              <Zap className="w-5 h-5 text-amber-500" fill="currentColor" /> Quick Post
            </Button>
          </div>
        </div>

        {/* Tab navigation — mirrors DriverNav */}
        <CustomerNav
          active={activeView}
          onChange={(k) => {
            setActiveView(k);
            setSearchParams(k === 'home' ? {} : { view: k });
            if (k === 'post') setShowForm(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          counts={{
            deliveries: deliveries.filter(d => ['pending','assigned','on_the_way'].includes(d.status)).length,
            messages: messagesUnread,
          }}
        />

        {/* Messages tab — central inbox for all conversations (offer threads). */}
        {activeView === 'messages' && (
          <div className="mb-6" data-testid="customer-messages-view">
            <MessagesInbox role="customer" onUnreadChange={setMessagesUnread} />
          </div>
        )}

        {/* Settings tab — customer email preferences (manage / unsubscribe). */}
        {activeView === 'settings' && (
          <div className="mb-6 max-w-2xl" data-testid="customer-settings-view">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 font-['Inter']">
              Settings
            </h1>
            <EmailPreferencesPanel />
          </div>
        )}

        {activeView === 'home' && (<>
        <NotificationPrompt role="customer" />

        <HomeHero
          onPost={() => { setShowForm(true); setActiveView('post'); }}
          onQuickPost={() => navigate('/quick-post')}
        />

        <HowItWorks />

        {DIRECTORY_ENABLED && <FindDriverCard onSearch={() => navigate('/drivers')} />}

        <WhyJustDeliver />

        {/* What Happens Next */}
        <Card className="mb-6 border border-gray-200 shadow-none overflow-hidden bg-white" data-testid="customer-instructions">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors"
            data-testid="toggle-instructions-btn"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <HelpCircle className="w-4 h-4 text-[#2E7D32]" /> What happens next?
            </span>
            {showInstructions ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showInstructions && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {[
                  { step: '1', title: 'Post Your Job', desc: 'Describe your delivery and add photos', icon: <Package className="w-4 h-4" /> },
                  { step: '2', title: 'Drivers Submit Offers', desc: 'Compare bids and choose the best price', icon: <Eye className="w-4 h-4" /> },
                  { step: '3', title: 'Pay Securely', desc: 'Pay via Stripe, driver details revealed', icon: <CreditCard className="w-4 h-4" /> },
                  { step: '4', title: 'Confirm Delivery', desc: 'Share the 6-digit code to verify delivery', icon: <KeyRound className="w-4 h-4" /> },
                ].map(({ step, title, desc, icon }) => (
                  <div key={step} className="flex-1 flex items-start gap-3 p-3 bg-gray-50 border border-gray-100 rounded-lg">
                    <div className="w-8 h-8 bg-[#2E7D32] text-white text-xs flex items-center justify-center rounded-full font-bold flex-shrink-0">{step}</div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">{icon} {title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <p className="font-semibold mb-1">Booking fee:</p>
                <p className="mb-1">The €10 JustDeliver.ie booking fee helps cover secure card payments, verified drivers, fraud prevention, customer support and cancellation refund protection.</p>
                <p className="text-amber-700/90">A flat <strong>€10</strong> on every booking — it is already included in the price you see. You pay it now to secure your booking, then pay the rest directly to your driver. You always see both amounts before paying.</p>
              </div>
            </div>
          )}
        </Card>

        {/* Quick settings — notification toggle */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg border border-gray-200 bg-white" data-testid="customer-notification-settings">
          <div>
            <p className="text-sm font-semibold text-[#050505]">Push notifications</p>
            <p className="text-xs text-gray-500 mt-0.5">Stay updated on bids, driver arrivals and deliveries.</p>
          </div>
          <NotificationToggle />
        </div>
        </>)}

        {activeView === 'post' && (
        <>
        {/* Create Delivery Form */}
        {showForm && (
          <Card className="mb-6 border border-gray-200 shadow-none" data-testid="delivery-form-card">
            <CardHeader className="border-b border-gray-100 py-5">
              <PostDeliveryHeader
                onBack={() => { setActiveView('home'); setSearchParams({}); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                done={stepDone}
                current={step}
                onStepClick={(n) => { if (n < step) { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
              />
              <p className="text-sm text-gray-600 mt-3" data-testid="post-job-subtitle">
                {['Step 1 of 5 — tell us where the driver collects from.', 'Step 2 of 5 — where is it going, and who receives it?', 'Step 3 of 5 — describe what you need moved.', 'Step 4 of 5 — your date, time and loading help.', 'Step 5 of 5 — add photos, then post your job.'][step - 1]}
              </p>
            </CardHeader>
            <CardContent className="pt-5">
              <form onSubmit={handleSubmit} className="space-y-5">
                {step === 1 && (<div className="space-y-5" data-testid="wizard-step-1">
                {/* Pickup Section */}
                <div>
                  <div className="flex items-start gap-3 mb-4" data-testid="post-section-pickup">
                    <span className="flex-shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center bg-orange-50 text-[#E65100] border-orange-100">
                      <MapPin className="w-5 h-5" strokeWidth={2} />
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <h2 className="text-base md:text-lg font-bold tracking-tight text-[#050505] leading-snug">Where are we collecting from?</h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5 leading-snug">Enter the pickup address and Eircode.</p>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-3 space-y-1">
                      <Label className="text-xs text-gray-500">Address</Label>
                      <AddressAutocomplete
                        value={formData.pickup_location}
                        onChange={(v) => setFormData(p => ({...p, pickup_location: v, pickup_lat: null, pickup_lng: null, distance_km: null, duration_min: null, route_geometry: null}))}
                        onSelect={({ place_name, lat, lng, county, eircode, area }) => setFormData(p => ({
                          ...p,
                          pickup_location: place_name,
                          pickup_lat: lat,
                          pickup_lng: lng,
                          pickup_county: (county && counties.includes(county)) ? county : p.pickup_county,
                          pickup_area: area ? area.slice(0, 30) : p.pickup_area,
                          pickup_eircode: eircode || p.pickup_eircode,
                          distance_km: null, duration_min: null, route_geometry: null,
                        }))}
                        placeholder="Search Irish address (street, town, eircode)…"
                        required
                        dataTestId="pickup-location-input"
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">County</Label>
                      <select value={formData.pickup_county} onChange={(e) => setFormData(p => ({...p, pickup_county: e.target.value}))} className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm" data-testid="pickup-county-select">
                        <option value="">Select county</option>
                        {counties.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500 flex items-center justify-between gap-2">
                        <span>Town / City / Area <span className="text-red-500">*</span></span>
                        <span className="text-[11px] tabular-nums text-gray-400" data-testid="pickup-area-counter">
                          {(formData.pickup_area || '').length}/30
                        </span>
                      </Label>
                      <Input
                        placeholder="Enter town, city or local area..."
                        value={formData.pickup_area || ''}
                        onChange={(e) => setFormData(p => ({ ...p, pickup_area: e.target.value.slice(0, 30) }))}
                        maxLength={30}
                        required
                        data-testid="pickup-area-input"
                        className="rounded-sm"
                      />
                      <p className="text-[11px] text-gray-500 leading-snug">Shown to drivers before they bid.</p>
                    </div>
                    <div className="sm:col-span-1 space-y-1 rounded-md border border-emerald-300 bg-emerald-50/60 p-2.5" data-testid="pickup-eircode-highlight">
                      <Label className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
                        <span aria-hidden="true">📍</span> Pickup Eircode <span className="text-[10px] font-normal text-emerald-700/80">(Recommended)</span>
                      </Label>
                      <Input
                        placeholder="e.g. D08 X9F2"
                        value={formData.pickup_eircode}
                        onChange={(e) => setFormData(p => ({...p, pickup_eircode: e.target.value.toUpperCase()}))}
                        data-testid="pickup-eircode-input"
                        className="rounded-sm bg-white border-emerald-300 focus-visible:border-emerald-500 focus-visible:border-2 focus-visible:ring-emerald-400/40"
                      />
                      <p className="text-[11px] text-emerald-800 leading-snug">
                        Providing an Eircode helps drivers calculate distance more accurately and usually results in faster, more accurate bids.
                      </p>
                    </div>
                  </div>
                </div>

                </div>)}

                {step === 2 && (<div className="space-y-5" data-testid="wizard-step-2">
                {/* Dropoff Section */}
                <div>
                  <div className="flex items-start gap-3 mb-4" data-testid="post-section-dropoff">
                    <span className="flex-shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center bg-emerald-50 text-[#2E7D32] border-emerald-100">
                      <MapPin className="w-5 h-5" strokeWidth={2} />
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <h2 className="text-base md:text-lg font-bold tracking-tight text-[#050505] leading-snug">Where is it going?</h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5 leading-snug">Enter the drop-off address and Eircode.</p>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-3 space-y-1">
                      <Label className="text-xs text-gray-500">Address</Label>
                      <AddressAutocomplete
                        value={formData.dropoff_location}
                        onChange={(v) => setFormData(p => ({...p, dropoff_location: v, dropoff_lat: null, dropoff_lng: null, distance_km: null, duration_min: null, route_geometry: null}))}
                        onSelect={({ place_name, lat, lng, county, eircode, area }) => setFormData(p => ({
                          ...p,
                          dropoff_location: place_name,
                          dropoff_lat: lat,
                          dropoff_lng: lng,
                          dropoff_county: (county && counties.includes(county)) ? county : p.dropoff_county,
                          dropoff_area: area ? area.slice(0, 30) : p.dropoff_area,
                          dropoff_eircode: eircode || p.dropoff_eircode,
                          distance_km: null, duration_min: null, route_geometry: null,
                        }))}
                        placeholder="Search Irish address (street, town, eircode)…"
                        required
                        dataTestId="dropoff-location-input"
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">County</Label>
                      <select value={formData.dropoff_county} onChange={(e) => setFormData(p => ({...p, dropoff_county: e.target.value}))} className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm" data-testid="dropoff-county-select">
                        <option value="">Select county</option>
                        {counties.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500 flex items-center justify-between gap-2">
                        <span>Town / City / Area <span className="text-red-500">*</span></span>
                        <span className="text-[11px] tabular-nums text-gray-400" data-testid="dropoff-area-counter">
                          {(formData.dropoff_area || '').length}/30
                        </span>
                      </Label>
                      <Input
                        placeholder="Enter town, city or local area..."
                        value={formData.dropoff_area || ''}
                        onChange={(e) => setFormData(p => ({ ...p, dropoff_area: e.target.value.slice(0, 30) }))}
                        maxLength={30}
                        required
                        data-testid="dropoff-area-input"
                        className="rounded-sm"
                      />
                      <p className="text-[11px] text-gray-500 leading-snug">Shown to drivers before they bid.</p>
                    </div>
                    <div className="sm:col-span-1 space-y-1 rounded-md border border-emerald-300 bg-emerald-50/60 p-2.5" data-testid="dropoff-eircode-highlight">
                      <Label className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
                        <span aria-hidden="true">📍</span> Drop-off Eircode <span className="text-[10px] font-normal text-emerald-700/80">(Recommended)</span>
                      </Label>
                      <Input
                        placeholder="e.g. T12 AB34"
                        value={formData.dropoff_eircode}
                        onChange={(e) => setFormData(p => ({...p, dropoff_eircode: e.target.value.toUpperCase()}))}
                        data-testid="dropoff-eircode-input"
                        className="rounded-sm bg-white border-emerald-300 focus-visible:border-emerald-500 focus-visible:border-2 focus-visible:ring-emerald-400/40"
                      />
                      <p className="text-[11px] text-emerald-800 leading-snug">
                        Providing an Eircode helps drivers calculate distance more accurately and usually results in faster, more accurate bids.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Route preview map (shown once both addresses are picked) */}
                {formData.pickup_lat && formData.pickup_lng && formData.dropoff_lat && formData.dropoff_lng && (
                  <div data-testid="customer-route-preview">
                    <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Navigation className="w-4 h-4 text-[#2E7D32]" /> Route preview
                      {(() => {
                        const wp = additionalDrops.filter(d => d && d.lat != null && d.lng != null);
                        return wp.length > 0 ? (
                          <span className="text-[10px] font-normal bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            {wp.length + 1} drops total
                          </span>
                        ) : null;
                      })()}
                    </p>
                    <RouteMap
                      pickup={{ lat: formData.pickup_lat, lng: formData.pickup_lng, label: formData.pickup_location }}
                      dropoff={{ lat: formData.dropoff_lat, lng: formData.dropoff_lng, label: formData.dropoff_location }}
                      waypoints={additionalDrops.filter(d => d && d.lat != null && d.lng != null).map(d => ({ lat: d.lat, lng: d.lng, label: d.address }))}
                      onRouteComputed={({ distance_km, duration_min, route_geometry }) =>
                        setFormData(p => ({ ...p, distance_km, duration_min, route_geometry }))
                      }
                    />
                  </div>
                )}

                {/* Multi-drop section — business accounts only */}
                {user?.account_type === 'business' && (
                  <div data-testid="multi-drop-section">
                    <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-800" /> Extra Drop-offs <span className="text-xs font-normal text-gray-500">(optional)</span>
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-3 text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold">How multi-drop works:</span> add more addresses below and drivers will see <span className="font-semibold">all</span> stops when they bid. One driver picks up once and delivers to every address in order — saving you the hassle (and cost) of posting separate jobs.
                    </div>
                    {additionalDrops.map((drop, i) => {
                      const updateDrop = (patch) =>
                        setAdditionalDrops(prev => prev.map((d, idx) =>
                          idx === i ? { ...(typeof d === 'string' ? { address: d } : d), ...patch } : d
                        ));
                      const dropObj = typeof drop === 'string' ? { address: drop } : (drop || {});
                      const dropPhotos = Array.isArray(dropObj.photos) ? dropObj.photos : [];
                      const onAddDropPhotos = async (e) => {
                        const files = Array.from(e.target.files || []).slice(0, 3 - dropPhotos.length);
                        if (!files.length) return;
                        const dataUrls = await Promise.all(files.map((f) => new Promise((resolve) => {
                          const reader = new FileReader();
                          reader.onload = () => resolve(reader.result);
                          reader.readAsDataURL(f);
                        })));
                        updateDrop({ photos: [...dropPhotos, ...dataUrls].slice(0, 3) });
                        e.target.value = '';
                      };
                      const removeDropPhoto = (pIdx) => {
                        updateDrop({ photos: dropPhotos.filter((_, idx) => idx !== pIdx) });
                      };
                      return (
                      <div
                        key={i}
                        className="mb-3 rounded-lg border border-gray-200 bg-white p-3.5 space-y-3"
                        data-testid={`multi-drop-card-${i}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2E7D32] text-white text-xs font-extrabold">
                              {i + 2}
                            </span>
                            Drop {i + 2}
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setAdditionalDrops(prev => prev.filter((_, idx) => idx !== i))}
                            data-testid={`remove-drop-btn-${i}`}
                            className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Remove
                          </Button>
                        </div>

                        {/* Address + County + Eircode */}
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-500">Drop address</Label>
                            <AddressAutocomplete
                              value={dropObj.address || ''}
                              onChange={(v) => updateDrop({ address: v, lat: null, lng: null, county: dropObj.county || '', eircode: '' })}
                              onSelect={({ place_name, lat, lng, county, eircode, area }) =>
                                updateDrop({ address: place_name, lat, lng, county, eircode, area })}
                              placeholder={`Search Irish address for drop ${i + 2}…`}
                              dataTestId={`additional-drop-input-${i}`}
                              className="rounded-sm"
                            />
                          </div>
                          <div className="grid gap-2 sm:grid-cols-2">
                            <div className="space-y-1">
                              <Label className="text-[11px] text-gray-500 flex items-center gap-1.5">
                                County <span className="text-red-500">*</span>
                              </Label>
                              <select
                                value={dropObj.county || ''}
                                onChange={(e) => updateDrop({ county: e.target.value })}
                                className="w-full border border-gray-200 rounded-sm px-3 h-9 text-sm bg-white"
                                data-testid={`drop-county-${i}`}
                              >
                                <option value="">Select county…</option>
                                {counties.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[11px] text-gray-500 flex items-center gap-1.5">
                                Eircode <span className="text-gray-400">(optional)</span>
                              </Label>
                              <Input
                                value={dropObj.eircode || ''}
                                onChange={(e) => updateDrop({ eircode: e.target.value.toUpperCase().slice(0, 8) })}
                                placeholder="e.g. D02 X285"
                                className="rounded-sm h-9 text-sm tracking-wider uppercase font-mono"
                                maxLength={8}
                                data-testid={`drop-eircode-${i}`}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Receiver details for this specific drop */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-gray-500" /> Receiver at Drop {i + 2}
                          </p>
                          <div className="grid gap-2 sm:grid-cols-3">
                            <div className="space-y-1">
                              <Label className="text-[11px] text-gray-500">Name</Label>
                              <Input
                                value={dropObj.receiver_name || ''}
                                onChange={(e) => updateDrop({ receiver_name: e.target.value })}
                                placeholder="Full name"
                                className="rounded-sm h-9 text-sm"
                                data-testid={`drop-receiver-name-${i}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[11px] text-gray-500">Phone</Label>
                              <Input
                                value={dropObj.receiver_phone || ''}
                                onChange={(e) => updateDrop({ receiver_phone: e.target.value })}
                                placeholder="Phone number"
                                className="rounded-sm h-9 text-sm"
                                data-testid={`drop-receiver-phone-${i}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[11px] text-gray-500">Email <span className="text-gray-400">(verification code)</span></Label>
                              <Input
                                value={dropObj.receiver_email || ''}
                                onChange={(e) => updateDrop({ receiver_email: e.target.value })}
                                type="email"
                                placeholder="Email for code"
                                className="rounded-sm h-9 text-sm"
                                data-testid={`drop-receiver-email-${i}`}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Description for this drop (optional) */}
                        <div className="space-y-1">
                          <Label className="text-[11px] text-gray-500">
                            What's being dropped here? <span className="text-gray-400">(optional)</span>
                          </Label>
                          <Input
                            value={dropObj.item_description || ''}
                            onChange={(e) => updateDrop({ item_description: e.target.value.slice(0, 250) })}
                            placeholder='e.g. "Box of brochures — leave at reception"'
                            maxLength={250}
                            className={`rounded-sm h-9 text-sm ${dropObj.item_description && containsContactInfo(dropObj.item_description) ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                            data-testid={`drop-description-${i}`}
                          />
                          {dropObj.item_description && containsContactInfo(dropObj.item_description) ? (
                            <p className="text-[10px] text-red-600 font-medium" data-testid={`drop-description-error-${i}`}>
                              No phone numbers, emails, URLs, or @handles. Contact details are shared after booking.
                            </p>
                          ) : (
                            <p className="text-[10px] text-gray-400">
                              Leave blank to use the main item description for this stop.
                            </p>
                          )}
                        </div>

                        {/* Dimensions & weight for this drop (optional) */}
                        <div className="space-y-1.5" data-testid={`drop-dimensions-${i}`}>
                          <Label className="text-[11px] text-gray-500 flex items-center gap-1.5">
                            <Ruler className="w-3.5 h-3.5 text-gray-500" />
                            Dimensions for this drop <span className="text-gray-400">(optional)</span>
                          </Label>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                              { key: 'dimensions_length', label: 'L (cm)' },
                              { key: 'dimensions_width', label: 'W (cm)' },
                              { key: 'dimensions_height', label: 'H (cm)' },
                              { key: 'weight_kg', label: 'Wt (kg)' },
                            ].map(({ key, label }) => (
                              <div key={key} className="space-y-0.5">
                                <span className="text-[10px] text-gray-400">{label}</span>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={dropObj[key] ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    updateDrop({ [key]: v === '' ? null : Math.max(0, parseFloat(v) || 0) });
                                  }}
                                  placeholder="0"
                                  className="rounded-sm h-9 text-sm tabular-nums"
                                  data-testid={`drop-${key}-${i}`}
                                />
                              </div>
                            ))}
                          </div>
                          <p className="text-[10px] text-gray-400">
                            Leave blank to use the main item dimensions for this drop.
                          </p>
                        </div>

                        {/* Per-drop photos (up to 3) */}
                        <div className="space-y-1.5" data-testid={`drop-photos-${i}`}>
                          <Label className="text-[11px] text-gray-500 flex items-center gap-1.5">
                            <Camera className="w-3.5 h-3.5 text-gray-500" />
                            Photos for this drop <span className="text-gray-400">(optional · up to 3)</span>
                          </Label>
                          <p className="text-[10px] text-gray-400 -mt-0.5">
                            Helps the driver identify what to drop here and where.
                          </p>
                          <div className="flex flex-wrap gap-2 pt-1">
                            {dropPhotos.map((src, pIdx) => (
                              <div
                                key={pIdx}
                                className="relative w-20 h-20 rounded-md border border-gray-200 overflow-hidden bg-gray-50"
                                data-testid={`drop-photo-thumb-${i}-${pIdx}`}
                              >
                                <img src={src} alt="" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeDropPhoto(pIdx)}
                                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white text-xs flex items-center justify-center hover:bg-black"
                                  aria-label="Remove photo"
                                  data-testid={`drop-photo-remove-${i}-${pIdx}`}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            {dropPhotos.length < 3 && (
                              <label
                                className="w-20 h-20 rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-[11px] text-gray-500 hover:border-[#2E7D32] hover:text-[#1B5E20] cursor-pointer transition-colors"
                                data-testid={`drop-photo-add-${i}`}
                              >
                                <Camera className="w-4 h-4 mb-0.5" />
                                Add photo
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={onAddDropPhotos}
                                />
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setAdditionalDrops(prev => [...prev, { address: '', lat: null, lng: null, county: '', eircode: '' }])}
                      data-testid="add-drop-btn"
                      className="rounded-md bg-[#1B5E20] hover:bg-[#0E3D11] text-white font-semibold border-2 border-[#F57C00] ring-1 ring-[#F57C00]/30 hover:ring-[#F57C00]/60 shadow-sm transition-all"
                    >
                      + Add another drop
                    </Button>
                  </div>
                )}

                {/* Receiver Details */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><User className="w-4 h-4 text-gray-800" /> Receiver Details</p>
                  <p className="text-xs text-gray-500 mb-3">The receiver will get the 6-digit verification code via email. Share it with the driver once the item is delivered to confirm completion.</p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Receiver Name</Label>
                      <Input placeholder="Full name" value={formData.receiver_name} onChange={(e) => setFormData(p => ({...p, receiver_name: e.target.value}))} data-testid="receiver-name-input" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Receiver Phone</Label>
                      <Input placeholder="Phone number" value={formData.receiver_phone} onChange={(e) => setFormData(p => ({...p, receiver_phone: e.target.value}))} data-testid="receiver-phone-input" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Receiver Email</Label>
                      <Input placeholder="Email for verification code" type="email" value={formData.receiver_email} onChange={(e) => setFormData(p => ({...p, receiver_email: e.target.value}))} data-testid="receiver-email-input" className="rounded-sm" />
                    </div>
                  </div>
                </div>

                </div>)}

                {step === 3 && (<div className="space-y-5" data-testid="wizard-step-3">
                {/* Item Details */}
                <div>
                  <div className="flex items-start gap-3 mb-4" data-testid="post-section-item">
                    <span className="flex-shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center bg-blue-50 text-[#1565C0] border-blue-100">
                      <Package className="w-5 h-5" strokeWidth={2} />
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <h2 className="text-base md:text-lg font-bold tracking-tight text-[#050505] leading-snug">What are we delivering?</h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5 leading-snug">Pick the item type and describe your item(s).</p>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-sm font-semibold text-[#374151]">What are we delivering?</Label>
                      <p className="text-xs text-gray-500 mb-2">Choose the type of item you&apos;re moving.</p>
                      <ItemTypeTiles
                        types={ITEM_TYPES}
                        value={formData.item_type}
                        onChange={(v) => setFormData(p => ({ ...p, item_type: v }))}
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-xs text-gray-500">Description</Label>
                      <Textarea
                        placeholder="Describe item(s) — what is it, any special handling?"
                        value={formData.item_description}
                        onChange={(e) => setFormData(p => ({...p, item_description: e.target.value.slice(0, DESCRIPTION_MAX)}))}
                        required
                        maxLength={DESCRIPTION_MAX}
                        data-testid="item-description-input"
                        className={`rounded-sm min-h-[80px] ${validateDescription(formData.item_description) ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                      />
                      <div className="flex items-start justify-between gap-3">
                        <p
                          className={`text-[11px] flex-1 ${validateDescription(formData.item_description) ? 'text-red-600' : 'text-gray-500'}`}
                          data-testid="item-description-helper"
                        >
                          {validateDescription(formData.item_description) || (
                            <>
                              <span aria-hidden="true">💡</span> <strong>Example:</strong> "Fridge delivery from Cork to Dublin" or "10 pallets from Galway to Limerick". Contact details are automatically shared after payment, so please don't include phone numbers, email addresses, websites, or social media usernames.
                            </>
                          )}
                        </p>
                        <span
                          className={`text-[11px] tabular-nums flex-shrink-0 ${formData.item_description.length > DESCRIPTION_MAX || (formData.item_description.length > 0 && formData.item_description.length < DESCRIPTION_MIN) ? 'text-red-600 font-medium' : 'text-gray-400'}`}
                          data-testid="item-description-counter"
                        >
                          {formData.item_description.length} / {DESCRIPTION_MAX}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dimensions */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2"><Ruler className="w-4 h-4 text-gray-600" /> Dimensions <span className="text-xs font-normal text-gray-400">(optional)</span></p>
                  <p className="text-xs text-gray-500 mb-3" data-testid="dimensions-helper">
                    <span aria-hidden="true">📏</span> Don't know the exact measurements? No problem. Uploading photos helps drivers give more accurate prices.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Length (cm)</Label>
                      <Input type="number" step="0.1" placeholder="L" value={formData.dimensions_length} onChange={(e) => setFormData(p => ({...p, dimensions_length: e.target.value}))} data-testid="dimension-length" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Width (cm)</Label>
                      <Input type="number" step="0.1" placeholder="W" value={formData.dimensions_width} onChange={(e) => setFormData(p => ({...p, dimensions_width: e.target.value}))} data-testid="dimension-width" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Height (cm)</Label>
                      <Input type="number" step="0.1" placeholder="H" value={formData.dimensions_height} onChange={(e) => setFormData(p => ({...p, dimensions_height: e.target.value}))} data-testid="dimension-height" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Weight (kg)</Label>
                      <Input type="number" step="0.1" placeholder="kg" value={formData.weight_kg} onChange={(e) => setFormData(p => ({...p, weight_kg: e.target.value}))} data-testid="weight-input" className="rounded-sm" />
                    </div>
                  </div>
                </div>

                </div>)}

                {step === 4 && (<div className="space-y-5" data-testid="wizard-step-4">
                {/* Delivery Window */}
                <div>
                  <div className="flex items-start gap-3 mb-4" data-testid="post-section-when">
                    <span className="flex-shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center bg-amber-50 text-amber-700 border-amber-100">
                      <Clock className="w-5 h-5" strokeWidth={2} />
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <h2 className="text-base md:text-lg font-bold tracking-tight text-[#050505] leading-snug">When do you need it there?</h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5 leading-snug">Choose your delivery date and time window.</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[11px] text-gray-400">Date</Label>
                        <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="delivery-date-input" className="rounded-sm" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-gray-400">Timing</Label>
                        <select value={deliveryTimeType} onChange={(e) => setDeliveryTimeType(e.target.value)} className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm" data-testid="delivery-time-type">
                          <option value="before">Before a specific time</option>
                          <option value="range">Time range</option>
                          <option value="flexible">Flexible / Any time</option>
                        </select>
                      </div>
                    </div>
                    {deliveryTimeType === 'before' && (
                      <div className="space-y-1">
                        <Label className="text-[11px] text-gray-400">Deliver before</Label>
                        <ClockTimePicker value={deliveryTimeBefore} onChange={setDeliveryTimeBefore} label="deliver before" />
                      </div>
                    )}
                    {deliveryTimeType === 'range' && (
                      <div className="flex items-end gap-3 flex-wrap">
                        <div className="space-y-1">
                          <Label className="text-[11px] text-gray-400">From</Label>
                          <ClockTimePicker value={deliveryTimeFrom} onChange={setDeliveryTimeFrom} label="from" />
                        </div>
                        <span className="text-gray-400 text-sm pb-2">to</span>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-gray-400">To</Label>
                          <ClockTimePicker value={deliveryTimeTo} onChange={setDeliveryTimeTo} label="to" />
                        </div>
                      </div>
                    )}
                    {deliveryTimeType === 'flexible' && (
                      <p className="text-xs text-gray-400 italic">Driver can deliver any time on the selected date</p>
                    )}
                  </div>
                </div>

                {/* Lifting & Loading Help */}
                <div data-testid="lifting-help-section">
                  <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-600" /> Lifting &amp; loading help
                  </p>
                  <p className="text-xs text-gray-500 mb-3" data-testid="lifting-help-tagline">
                    <span aria-hidden="true">😃</span> Honest answers = faster collections and fairer prices.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <HelpChoice
                      label="At pickup (collection)"
                      value={formData.collection_help}
                      onChange={(v) => setFormData(p => ({...p, collection_help: v}))}
                      options={[
                        { value: 'available', emoji: '✅', text: 'Someone will help load 😃' },
                        { value: 'none',      emoji: '❌', text: 'No help available 😬' },
                      ]}
                      dataTestId="collection-help"
                    />
                    <HelpChoice
                      label="At drop-off (delivery)"
                      value={formData.delivery_help}
                      onChange={(v) => setFormData(p => ({...p, delivery_help: v}))}
                      options={[
                        { value: 'available', emoji: '✅', text: 'Someone will help unload 😃' },
                        { value: 'none',      emoji: '❌', text: 'No help available 😬' },
                      ]}
                      dataTestId="delivery-help"
                    />
                  </div>

                  <div className="mt-4">
                    <HelpChoice
                      label="Driver assistance needed?"
                      value={formData.driver_assistance}
                      onChange={(v) => setFormData(p => ({...p, driver_assistance: v}))}
                      options={[
                        { value: 'driver_only',     emoji: '🚚', text: 'Driver only' },
                        { value: 'helper_required', emoji: '👥', text: 'Driver must bring a helper' },
                      ]}
                      dataTestId="driver-assistance"
                    />
                    <p className="text-xs text-gray-500 mt-2" data-testid="driver-assistance-helper">
                      <span aria-hidden="true">💪</span> Larger items may require two people and could cost more.
                    </p>
                  </div>
                </div>

                {/* Optional Customer Budget — guide price only.
                    Not a maximum, not required. Drivers see this on their
                    bid view to help them price closer to expectations. */}
                <div data-testid="customer-budget-section">
                  <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                    <span aria-hidden="true">💶</span> Your Budget (€) <span className="text-xs font-normal text-gray-400">(optional)</span>
                  </p>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    value={formData.customer_budget}
                    onChange={(e) => {
                      // Positive numbers only — strip minus signs / decimals-below-zero.
                      const raw = e.target.value;
                      if (raw === '' || raw === null) {
                        setFormData(p => ({ ...p, customer_budget: '' }));
                        return;
                      }
                      const n = parseFloat(raw);
                      if (Number.isFinite(n) && n >= 0) {
                        setFormData(p => ({ ...p, customer_budget: raw }));
                      }
                    }}
                    placeholder="e.g. 85"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2E7D32]"
                    data-testid="customer-budget-input"
                  />
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed" data-testid="customer-budget-helper">
                    Optional: Enter what you&apos;d ideally like to spend for this delivery. This is only a guide and is not a maximum price. Drivers will try to bid as close to your budget as possible or provide their best price if your budget isn&apos;t realistic for the journey.
                  </p>
                </div>

                </div>)}

                {step === 5 && (<div className="space-y-5" data-testid="wizard-step-5">
                {/* Photos */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2"><Camera className="w-4 h-4 text-gray-600" /> Photos <span className="text-xs font-normal text-gray-400">(up to 5)</span></p>
                  <p className="text-xs text-gray-500 mb-3" data-testid="photos-helper">
                    <span aria-hidden="true">📸</span> Photos usually mean quicker offers and more accurate pricing.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {photoPreviews.map((src, i) => (
                      <div key={i} className="relative w-20 h-20 border border-gray-200 overflow-hidden group">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(i)} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {photoFiles.length < 5 && (
                      <label className="w-20 h-20 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#2E7D32] transition-colors" data-testid="photo-upload-btn">
                        <Camera className="w-5 h-5 text-gray-400" />
                        <span className="text-[10px] text-gray-400 mt-1">Add</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                      </label>
                    )}
                  </div>
                </div>

                <div className="pt-2 space-y-4">
                  {/* "📋 Before You Post" checklist */}
                  <div
                    className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4"
                    data-testid="before-you-post"
                  >
                    <p className="text-sm font-bold text-[#1B5E20] mb-2 flex items-center gap-2">
                      <span aria-hidden="true">📋</span> Before You Post
                    </p>
                    <ul className="space-y-1.5 text-sm text-gray-700">
                      {[
                        'Photos help drivers quote accurately',
                        'No prohibited or illegal items',
                        'No contact details in descriptions',
                        'Drivers are independent contractors',
                      ].map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2"
                          data-testid={`before-you-post-item-${i + 1}`}
                        >
                          <span className="text-[#2E7D32] font-bold flex-shrink-0">✅</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Trust / "Your Peace of Mind" block */}
                  <div
                    className="rounded-lg border border-amber-100 bg-amber-50/40 p-4"
                    data-testid="trust-block"
                  >
                    <p className="text-sm font-bold text-[#E65100] mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#F57C00]" /> Your Peace of Mind
                    </p>
                    <ul className="space-y-1.5 text-sm text-gray-700">
                      {[
                        'No obligation to accept any offer',
                        'Compare driver prices before paying',
                        <>Secure payments powered by <span className="font-semibold text-[#635BFF]">Stripe</span></>,
                      ].map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2"
                          data-testid={`trust-block-item-${i + 1}`}
                        >
                          <span className="text-[#2E7D32] font-bold flex-shrink-0">✅</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-[#2E7D32]"
                      data-testid="job-terms-checkbox"
                    />
                    <span className="text-sm text-gray-700">
                      <span aria-hidden="true">✅</span> I have read and agree to the{' '}
                      <a href="/terms" target="_blank" className="text-[#2E7D32] font-medium hover:underline">Terms &amp; Conditions</a>
                    </span>
                  </label>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      className="flex-1 bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-md h-12 text-base font-bold shadow-sm hover:shadow-md transition-all active:scale-[0.99]"
                      disabled={submitting}
                      data-testid="submit-delivery-btn"
                    >
                      {submitting ? 'Submitting…' : (
                        <>
                          <span aria-hidden="true" className="mr-1.5">🚚</span> Post Job &amp; Get Offers
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowForm(false); setActiveView('home'); setSearchParams({}); }}
                      className="border-gray-300 rounded-md h-12 sm:w-32"
                      data-testid="cancel-delivery-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
                </div>)}

                {/* Wizard navigation */}
                <div className="flex items-center gap-3 pt-1" data-testid="wizard-nav">
                  {step > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={goPrevStep}
                      className="border-gray-300 rounded-full h-12 px-6 font-semibold"
                      data-testid="wizard-back-btn"
                    >
                      Back
                    </Button>
                  )}
                  {step < 5 && (
                    <Button
                      type="button"
                      onClick={goNextStep}
                      className="flex-1 bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-full h-12 text-base font-bold shadow-sm hover:shadow-md transition-all active:scale-[0.99]"
                      data-testid="wizard-next-btn"
                    >
                      Continue
                    </Button>
                  )}
                  <span className="text-xs text-gray-400 font-semibold tabular-nums flex-shrink-0" data-testid="wizard-step-count">
                    Step {step} of 5
                  </span>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
        {!showForm && (
          <Card className="border border-gray-200 shadow-none" data-testid="post-empty-state">
            <CardContent className="py-10 text-center">
              <Truck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-4">Ready to post a new delivery?</p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-2 rounded-sm"
                data-testid="open-form-btn"
              >
                <Plus className="w-4 h-4" /> Open Delivery Form
              </Button>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {activeView === 'deliveries' && (<>
        {/* Deliveries - Tabbed System */}
        <DeliveryTabs
          activeCount={deliveries.filter(d => ['pending','assigned','on_the_way'].includes(d.status) || (d.status === 'completed' && d.assigned_driver_id && !reviewedDeliveries[d.id])).length}
          expiredCount={deliveries.filter(d => d.status === 'expired').length}
          onSelectJob={handleSelectJob}
        >
          {/* Active tab content — existing delivery cards.
              PR: also surface just-completed jobs that the customer hasn't
              rated yet, so the QuickReview widget is visible where the
              customer expects it. Reviewed jobs fall through to History. */}
          {loading ? (
            <div className="flex items-center justify-center py-12"><Package className="w-8 h-8 text-gray-400 animate-pulse" /></div>
          ) : deliveries.filter(d => ['pending','assigned','on_the_way'].includes(d.status) || (d.status === 'completed' && d.assigned_driver_id && !reviewedDeliveries[d.id])).length === 0 ? (
            <div className="text-center py-14 px-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60" data-testid="empty-active-state">
              <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mx-auto mb-3">
                <Package className="w-6 h-6 text-[#2E7D32]" />
              </div>
              <p className="text-base font-bold text-slate-900">No active deliveries</p>
              <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">Post a job and drivers across Ireland will start bidding within minutes.</p>
              <Button onClick={() => { setActiveView('post'); setSearchParams({ view: 'post' }); setShowForm(true); }} className="mt-5 bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-2 rounded-full h-11 px-6 font-bold shadow-sm hover:shadow-md transition-all" data-testid="empty-active-cta">
                <Truck className="w-4 h-4" /> Post a Job
              </Button>
            </div>
          ) : (() => {
            const activeDeliveries = deliveries.filter(d => ['pending','assigned','on_the_way'].includes(d.status) || (d.status === 'completed' && d.assigned_driver_id && !reviewedDeliveries[d.id]));
            const DELIVERIES_PER_PAGE = 10;
            const totalCustPages = Math.ceil(activeDeliveries.length / DELIVERIES_PER_PAGE);
            const pagedDeliveries = activeDeliveries.slice((custDeliveryPage - 1) * DELIVERIES_PER_PAGE, custDeliveryPage * DELIVERIES_PER_PAGE);
            return (
            <>
            <div className="space-y-4">
            {pagedDeliveries.map((delivery) => {
              const STEPS = [
                { key: 'pending', label: 'Posted' },
                { key: 'bidding', label: 'Drivers Bidding' },
                { key: 'assigned', label: 'Driver Assigned' },
                { key: 'on_the_way', label: 'On the Way' },
                { key: 'completed', label: 'Delivered' },
              ];
              const statusIndex = delivery.status === 'pending' && delivery.offer_count > 0 ? 1
                : delivery.status === 'pending' ? 0
                : delivery.status === 'paid' || delivery.status === 'assigned' ? 2
                : delivery.status === 'on_the_way' ? 3
                : delivery.status === 'completed' ? 4 : 0;
              const isExpired = delivery.status === 'expired';

              const helperTexts = [
                'Your job is live — waiting for driver offers',
                `${delivery.offer_count || 0} driver${(delivery.offer_count || 0) !== 1 ? 's' : ''} submitted offers — view and compare`,
                'Your driver is confirmed and preparing for pickup',
                `Driver is en route ${delivery.estimated_arrival ? `— ETA ${delivery.estimated_arrival}` : '— arriving soon'}`,
                'Delivery completed successfully',
              ];

              const STATUS_PILLS = [
                { label: 'Awaiting Bids', pill: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
                { label: 'Drivers Bidding', pill: 'bg-emerald-50 text-emerald-800 border-emerald-300', dot: 'bg-emerald-600 animate-pulse' },
                { label: 'Driver Assigned', pill: 'bg-blue-50 text-blue-800 border-blue-200', dot: 'bg-blue-600' },
                { label: 'On the Way', pill: 'bg-indigo-50 text-indigo-800 border-indigo-200', dot: 'bg-indigo-600' },
                { label: 'Delivered', pill: 'bg-slate-100 text-slate-800 border-slate-200', dot: 'bg-emerald-700' },
              ];
              const statusPill = STATUS_PILLS[statusIndex] || STATUS_PILLS[0];

              return (
              <Card key={delivery.id} className="rounded-2xl border border-slate-200/90 shadow-sm hover:border-slate-300 hover:shadow-md transition-all duration-200 overflow-hidden bg-white" data-testid={`delivery-card-${delivery.id}`}>
                <CardContent className="p-0">
                  {/* Header strip — reference, timing, status */}
                  <div className="px-4 sm:px-5 py-3 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      {delivery.delivery_ref && (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold text-slate-700 bg-white px-2.5 py-1 rounded-lg border border-slate-200" data-testid={`delivery-ref-${delivery.id}`}>
                          <Hash className="w-3 h-3 text-slate-400" />{delivery.delivery_ref}
                        </span>
                      )}
                      <DeliveryTimeBadge preferredTime={delivery.preferred_time} size="sm" dataTestId={`delivery-time-badge-${delivery.id}`} />
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${isExpired ? 'bg-rose-50 text-rose-700 border-rose-200' : statusPill.pill}`}
                      data-testid={`delivery-status-pill-${delivery.id}`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${isExpired ? 'bg-rose-500' : statusPill.dot}`} />
                      {isExpired ? 'Expired' : statusPill.label}
                    </span>
                  </div>

                  {/* Status Progress Bar */}
                  <div className="px-4 sm:px-5 pt-3 pb-3" data-testid={`delivery-status-${delivery.id}`}>
                    {isExpired ? (
                      <div className="flex items-center justify-between gap-2 mb-1" data-testid={`delivery-expired-banner-${delivery.id}`}>
                        <p className="text-xs font-semibold text-rose-700">Request expired</p>
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Archived</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-5 gap-1.5 mb-2">
                        {STEPS.map((step, i) => (
                          <div
                            key={step.key}
                            className={`h-1.5 rounded-full transition-all duration-300 ${i <= statusIndex ? 'bg-gradient-to-r from-[#2E7D32] to-[#4CAF50]' : 'bg-slate-100'}`}
                          />
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-500" data-testid={`delivery-helper-${delivery.id}`}>
                      {isExpired
                        ? 'Your delivery request expired due to inactivity. You can repost anytime.'
                        : helperTexts[statusIndex]}
                    </p>
                  </div>

                  <div className="p-4 sm:p-5 pt-1">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Item thumbnail — the customer's own photo, with a
                          count badge when they uploaded more than one. */}
                      {delivery.photos && delivery.photos.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => window.open(resolveAssetUrl(delivery.photos[0]), '_blank')}
                          className="group relative flex-shrink-0 w-full sm:w-28 h-32 sm:h-28 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 hover:border-[#2E7D32]/50 hover:shadow-md transition-all duration-200"
                          title="View photo"
                          data-testid={`delivery-thumb-${delivery.id}`}
                        >
                          <img
                            src={resolveAssetUrl(delivery.photos[0])}
                            alt={delivery.item_description || 'Delivery item'}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                            loading="lazy"
                          />
                          <span className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/45 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          {delivery.photos.length > 1 && (
                            <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[10px] font-bold backdrop-blur-sm">
                              <Camera className="w-3 h-3" /> {delivery.photos.length}
                            </span>
                          )}
                        </button>
                      ) : (
                        <div
                          className="hidden sm:flex flex-shrink-0 w-28 h-28 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 items-center justify-center"
                          data-testid={`delivery-thumb-placeholder-${delivery.id}`}
                          title="No photo added"
                        >
                          <Camera className="w-6 h-6 text-slate-300" />
                        </div>
                      )}

                      {/* Main info */}
                      <div className="flex-1 min-w-0">
                        {/* Route — vertical journey stepper */}
                        <div className="relative pl-6 py-1 mb-3">
                          <div className="absolute left-[9px] top-3 bottom-3 border-l-2 border-dashed border-slate-200" />
                          <div className="relative space-y-3">
                            <div className="relative">
                              <span className="absolute -left-6 top-1 w-3.5 h-3.5 rounded-full bg-[#2E7D32] ring-4 ring-emerald-50" />
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#2E7D32]">Pickup</p>
                              <p className="text-sm font-semibold text-slate-900 truncate">{delivery.pickup_location}{delivery.pickup_county && <span className="text-slate-400 font-normal"> ({delivery.pickup_county})</span>}</p>
                            </div>
                            <div className="relative">
                              <span className="absolute -left-6 top-1 w-3.5 h-3.5 rounded-full bg-[#1565C0] ring-4 ring-blue-50" />
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#1565C0]">Drop-off</p>
                              <p className="text-sm font-semibold text-slate-900 truncate">{delivery.dropoff_location}{delivery.dropoff_county && <span className="text-slate-400 font-normal"> ({delivery.dropoff_county})</span>}</p>
                            </div>
                          </div>
                        </div>

                        <p className="text-sm text-slate-600 truncate mb-2">{delivery.item_description}</p>

                        <div className="flex flex-wrap items-center gap-2">
                          {(delivery.payment_status === 'paid' || delivery.payment_status === 'fees_paid') && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                              <CheckCircle className="w-3 h-3" /> {delivery.payment_status === 'fees_paid' ? 'Fees Paid' : 'Paid'}
                            </span>
                          )}
                          {delivery.payment_method === 'direct' && delivery.payment_status === 'fees_paid' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full" data-testid={`direct-pay-pill-${delivery.id}`}>
                              <Banknote className="w-3 h-3" /> Pay driver directly: €{(delivery.direct_balance_due || delivery.assigned_price || 0).toFixed(2)}
                            </span>
                          )}
                          {delivery.item_type && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium capitalize rounded-full">{delivery.item_type}</span>
                          )}
                          {delivery.customer_budget != null && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-800 text-xs font-medium rounded-full border border-indigo-200"
                              title="Your guide price. Not a maximum — drivers may bid above or below."
                              data-testid={`customer-budget-pill-${delivery.id}`}
                            >
                              <span aria-hidden="true">💶</span> Budget €{Number(delivery.customer_budget).toFixed(0)} <span className="text-[10px] text-indigo-600 font-normal">(Guide)</span>
                            </span>
                          )}
                          {(delivery.dimensions_length || delivery.weight_kg) && (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              {delivery.dimensions_length && <><Ruler className="w-3 h-3" /> {delivery.dimensions_length}x{delivery.dimensions_width || '?'}x{delivery.dimensions_height || '?'}cm</>}
                              {delivery.weight_kg && <><Weight className="w-3 h-3 ml-1" /> {delivery.weight_kg}kg</>}
                            </span>
                          )}
                          {delivery.receiver_name && (
                            <span className="text-xs text-gray-400">Receiver: {delivery.receiver_name}</span>
                          )}
                        </div>

                        {/* Cancel a confirmed Pay Driver Directly booking */}
                        {delivery.payment_status === 'fees_paid' && delivery.status !== 'completed' && delivery.status !== 'cancelled' && (
                          <div className="mt-3" data-testid={`customer-cancel-wrap-${delivery.id}`}>
                            <CancelBookingButton mode="customer" delivery={delivery} onDone={() => fetchDeliveries()} />
                          </div>
                        )}

                        {/* Delivery Code */}
                        {delivery.delivery_code && ['paid', 'fees_paid'].includes(delivery.payment_status) && delivery.status !== 'completed' && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`delivery-code-${delivery.id}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <KeyRound className="w-4 h-4 text-amber-600" />
                              <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Delivery Code</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-2xl font-mono font-bold tracking-[0.3em] text-gray-900">{delivery.delivery_code}</span>
                              <button
                                onClick={() => { navigator.clipboard.writeText(delivery.delivery_code); toast.success('Code copied!'); }}
                                className="text-amber-600 hover:text-amber-800 p-1"
                                data-testid={`copy-code-btn-${delivery.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-xs text-amber-700 mt-1">Share this code with the driver once your item is delivered to confirm completion.</p>
                          </div>
                        )}

                        {/* Per-drop codes — multi-drop deliveries only */}
                        {Array.isArray(delivery.drops) && delivery.drops.length > 0 && ['paid', 'fees_paid'].includes(delivery.payment_status) && delivery.status !== 'completed' && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`per-drop-codes-${delivery.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <KeyRound className="w-4 h-4 text-amber-600" />
                              <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Drop Codes ({delivery.drops.length})</span>
                            </div>
                            <p className="text-[11px] text-amber-700 mb-2">Each intermediate stop has its own 6-digit code. Share each code with that drop's receiver — the driver must enter every code before completing the final delivery.</p>
                            <div className="space-y-1.5">
                              {delivery.drops.map((drop, idx) => (
                                <div key={idx} className="flex items-center justify-between gap-3 bg-white rounded px-2.5 py-1.5 border border-amber-100">
                                  <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-700 truncate">
                                      Drop #{idx + 1} {drop.receiver_name ? `· ${drop.receiver_name}` : ''}
                                    </p>
                                    <p className="text-[11px] text-gray-500 truncate">{drop.address}</p>
                                  </div>
                                  {drop.verified_at ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 whitespace-nowrap">
                                      <CheckCircle className="w-3.5 h-3.5" /> Verified
                                    </span>
                                  ) : drop.code ? (
                                    <button
                                      type="button"
                                      onClick={() => { navigator.clipboard.writeText(drop.code); toast.success(`Drop #${idx + 1} code copied!`); }}
                                      className="font-mono text-base font-bold tracking-[0.25em] text-gray-900 hover:text-amber-700 transition-colors"
                                      data-testid={`per-drop-code-${delivery.id}-${idx}`}
                                      title="Click to copy"
                                    >
                                      {drop.code}
                                    </button>
                                  ) : (
                                    <span className="text-[11px] text-gray-400">—</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Delivery proof */}
                        {delivery.delivery_proof_photo && delivery.status === 'completed' && (
                          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid={`delivery-proof-${delivery.id}`}>
                            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Verified Delivery</p>
                            <img src={resolveAssetUrl(delivery.delivery_proof_photo)} alt="Delivery proof" className="w-full max-w-[200px] h-auto border border-emerald-100 rounded" />
                          </div>
                        )}

                        {/* Download VAT invoice / receipt PDF */}
                        {delivery.capture_status === 'captured' && (
                          <a
                            href={downloadVatInvoiceUrl(delivery.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2E7D32] hover:text-[#1B5E20] hover:underline"
                            data-testid={`download-invoice-btn-${delivery.id}`}
                          >
                            <Download className="w-4 h-4" /> Download VAT invoice (PDF)
                          </a>
                        )}

                        {/* Review section */}
                        {delivery.status === 'completed' && delivery.assigned_driver_id && (
                          <div className="mt-3" data-testid={`review-section-${delivery.id}`}>
                            {reviewedDeliveries[delivery.id] ? (
                              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-xs font-bold text-yellow-800 uppercase tracking-wider mb-1 flex items-center gap-1"><Star className="w-3 h-3 fill-yellow-500 text-yellow-500" /> Your Review</p>
                                <StarRating rating={reviewedDeliveries[delivery.id].rating} size="w-4 h-4" />
                                {reviewedDeliveries[delivery.id].comment && <p className="text-sm text-gray-600 mt-1 italic">"{reviewedDeliveries[delivery.id].comment}"</p>}
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center gap-2">
                                <QuickReview
                                  delivery={delivery}
                                  onSubmitted={(review) => {
                                    setReviewedDeliveries((prev) => ({ ...prev, [delivery.id]: review }));
                                  }}
                                  onAddComment={(dlv, rating) => {
                                    setReviewDelivery(dlv);
                                    setReviewRating(rating);
                                    setReviewComment('');
                                    setReviewDialogOpen(true);
                                  }}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openReviewDialog(delivery)}
                                  className="text-xs text-yellow-700 hover:text-yellow-900 hover:bg-yellow-50 gap-1"
                                  data-testid={`review-btn-${delivery.id}`}
                                >
                                  Add a comment
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right side: Driver info + Actions */}
                      <div className="flex flex-col items-end gap-3 flex-shrink-0">
                        {delivery.assigned_driver_name && ['paid', 'authorized', 'fees_paid'].includes(delivery.payment_status) && delivery.status !== 'completed' ? (
                          <div className="w-full sm:w-64">
                            <ContactCard
                              name={delivery.assigned_driver_name}
                              phone={delivery.assigned_driver_phone}
                              role="Driver"
                              isPremium={delivery.assigned_driver_is_premium}
                              subtitle={delivery.payment_method === 'direct' && delivery.payment_status === 'fees_paid'
                                ? `Pay driver directly: €${(delivery.direct_balance_due || delivery.assigned_price || 0).toFixed(2)}`
                                : `Total paid: €${(delivery.assigned_price || 0).toFixed(2)}`}
                              dataTestId={`driver-contact-${delivery.id}`}
                            />
                          </div>
                        ) : delivery.assigned_driver_name && delivery.payment_status === 'paid' ? (
                          <div className="text-right" data-testid={`driver-info-${delivery.id}`}>
                            <div className="flex items-center gap-1.5 justify-end flex-wrap">
                              <p className="font-medium text-sm">{delivery.assigned_driver_name}</p>
                              {delivery.assigned_driver_is_premium && <PremiumBadge size="sm" dataTestId={`driver-info-premium-${delivery.id}`} />}
                            </div>
                            <p className="text-lg font-bold text-[#2E7D32]">€{delivery.assigned_price?.toFixed(2)}</p>
                          </div>
                        ) : delivery.assigned_driver_name ? (
                          <div className="text-right">
                            <p className="text-sm text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Driver assigned</p>
                            <p className="text-lg font-bold">€{((delivery.assigned_price || 0) + (delivery.booking_deposit_amount ?? delivery.platform_fee ?? 10)).toFixed(2)}</p>
                            <p className="text-[10px] text-gray-500">Includes €10 JustDeliver.ie booking fee</p>
                          </div>
                        ) : null}

                        {/* Edit / Cancel actions — allowed while payment hasn't been authorised yet */}
                        {delivery.status === 'pending' && !['authorized', 'paid', 'fees_paid'].includes(delivery.payment_status) && (
                          <div className="flex gap-2 w-full" data-testid={`pending-actions-${delivery.id}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEditJob(delivery)}
                              className="flex-1 gap-1.5 border-gray-300 rounded-sm hover:bg-gray-50"
                              data-testid={`edit-job-btn-${delivery.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelJob(delivery)}
                              disabled={cancellingId === delivery.id}
                              className="flex-1 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-sm disabled:opacity-50"
                              data-testid={`cancel-job-btn-${delivery.id}`}
                            >
                              <X className="w-3.5 h-3.5" /> {cancellingId === delivery.id ? 'Cancelling…' : 'Cancel'}
                            </Button>
                          </div>
                        )}

                        {delivery.status === 'pending' && delivery.expires_at && !['authorized','paid','fees_paid'].includes(delivery.payment_status) && (
                          <div className="mb-3" data-testid={`card-expiry-${delivery.id}`}>
                            <ExpiryCountdown
                              expiresAt={delivery.expires_at}
                              variant="customer"
                              onExpire={() => fetchDeliveries()}
                            />
                          </div>
                        )}

                        {delivery.status === 'pending' && (
                          <Dialog open={offersDialogOpen && selectedDelivery?.id === delivery.id} onOpenChange={(open) => { if (!open) setOffersDialogOpen(false); }}>
                            <DialogTrigger asChild>
                              {delivery.offer_count > 0 ? (
                                <Button
                                  onClick={() => viewOffers(delivery)}
                                  className="relative gap-2 bg-gradient-to-r from-[#2E7D32] to-[#388E3C] text-white hover:from-[#1B5E20] hover:to-[#2E7D32] rounded-full h-11 px-6 font-bold shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
                                  data-testid={`view-offers-btn-${delivery.id}`}
                                >
                                  <Eye className="w-4 h-4" /> View Driver Offers ({delivery.offer_count})
                                  {(delivery.unread_message_count || 0) > 0 && (
                                    <span
                                      className="absolute -top-2 -right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F57C00] text-white text-[11px] font-bold shadow-md ring-2 ring-white animate-pulse"
                                      data-testid={`new-message-badge-${delivery.id}`}
                                    >
                                      <span aria-hidden="true">💬</span>
                                      {delivery.unread_message_count} new
                                    </span>
                                  )}
                                </Button>
                              ) : (
                                <Button variant="outline" disabled className="gap-2 border-slate-200 bg-slate-50 text-slate-400 rounded-full h-11 px-6 cursor-not-allowed font-semibold" data-testid={`view-offers-btn-${delivery.id}`}>
                                  <Clock className="w-4 h-4 animate-spin" style={{ animationDuration: '3s' }} /> Waiting for driver offers...
                                </Button>
                              )}
                            </DialogTrigger>
                          <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
                            <DialogHeader className="min-w-0">
                              <DialogTitle className="font-['Inter'] text-base sm:text-lg pr-6">Select a Driver &amp; Pay</DialogTitle>
                            </DialogHeader>

                            {/* Sub-heading right under the title */}
                            <p className="text-sm text-gray-700 -mt-1 mb-3 leading-snug" data-testid="select-driver-subhead">
                              <span aria-hidden="true">🚚</span> Drivers are competing for your job — choose the best offer.
                            </p>

                            {/* Premium expiry card — replaces the old red banner. Renders only
                                while the request is still actively pending payment. */}
                            {delivery.status === 'pending' && !['authorized','paid'].includes(delivery.payment_status) && delivery.expires_at && (
                              <div className="mb-4" data-testid={`offers-expiry-block-${delivery.id}`}>
                                <ExpiryCardPremium
                                  expiresAt={delivery.expires_at}
                                  createdAt={delivery.created_at}
                                  driverCount={offers.length}
                                  onExpire={() => fetchDeliveries()}
                                />
                              </div>
                            )}

                            {/* === DRIVER OFFERS (primary focus, above-the-fold) === */}
                            {offersLoading ? (
                              <div className="flex items-center justify-center py-8"><Package className="w-6 h-6 text-gray-400 animate-pulse" /></div>
                            ) : offers.length === 0 ? (
                              <div className="text-center py-8">
                                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-600">No offers yet</p>
                                <p className="text-sm text-gray-400 mt-1">Drivers will submit offers soon</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {offers.map((offer) => {
                                  const pricing = customerOfferPricing(offer);
                                  return (
                                  <div
                                    key={offer.id}
                                    className="rounded-xl border bg-white overflow-hidden border-slate-200 hover:border-[#2E7D32]/40 hover:shadow-md transition-all duration-200"
                                    data-testid={`offer-${offer.id}`}
                                  >
                                    {/* Driver identity row */}
                                    <div className="p-4 flex gap-3 items-start">
                                      <div className="flex-shrink-0" data-testid={`offer-avatar-${offer.id}`}>
                                        {offer.driver_profile_image ? (
                                          <img src={`${process.env.REACT_APP_BACKEND_URL}${offer.driver_profile_image}`} alt={offer.driver_name} className="w-12 h-12 rounded-full object-cover border-2 border-gray-200" onError={(e) => { e.target.onerror=null; e.target.style.display='none'; e.target.parentElement.querySelector('.offer-photo-fallback').style.display='flex'; }} />
                                        ) : null}
                                        <div className={`offer-photo-fallback w-12 h-12 rounded-full bg-gray-100 items-center justify-center border-2 border-gray-200`} style={{ display: offer.driver_profile_image ? 'none' : 'flex' }}>
                                          <User className="w-6 h-6 text-gray-400" />
                                        </div>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <p className="font-bold text-[15px] text-slate-900 font-mono tracking-tight" data-testid={`offer-driver-name-${offer.id}`}>{offer.driver_name}</p>
                                          {offer.driver_phone === 'Hidden until payment' && (
                                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide" data-testid={`offer-alias-hint-${offer.id}`}>Driver ID</span>
                                          )}
                                          {offer.driver_is_premium && <PremiumBadge size="sm" dataTestId={`offer-premium-${offer.id}`} />}
                                          {/* Active conversation indicator — show whenever the
                                              driver has sent ANY message (initial bid message
                                              or a thread reply), so customers don't miss it. */}
                                          {(offer.last_thread_sender_role === 'driver' || (!!offer.message && offer.thread_message_count === 0)) && (
                                            <span
                                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F57C00] text-white text-[10px] font-bold rounded whitespace-nowrap shadow-sm"
                                              data-testid={`offer-message-pill-${offer.id}`}
                                            >
                                              <span aria-hidden="true">💬</span>
                                              {offer.last_thread_sender_role === 'driver' ? 'Driver replied' : 'Driver asked'}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1" data-testid={`offer-rating-${offer.id}`}>
                                          {offer.driver_review_count > 0 ? (
                                            <>
                                              <StarRating rating={Math.round(offer.driver_avg_rating)} size="w-3.5 h-3.5" />
                                              <span className="text-sm font-medium text-gray-700">{offer.driver_avg_rating}</span>
                                              <span className="text-xs text-gray-400">({offer.driver_review_count} review{offer.driver_review_count !== 1 ? 's' : ''})</span>
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-400 italic">New driver</span>
                                          )}
                                        </div>
                                        {/* Completed-deliveries pill — anonymous social proof
                                            shown to customers only. Excludes cancelled/expired
                                            jobs (backend filter: status === 'completed'). */}
                                        <div className="mt-1" data-testid={`offer-completed-deliveries-${offer.id}`}>
                                          {(offer.driver_completed_deliveries || 0) > 0 ? (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-800 text-[11px] font-medium rounded border border-emerald-100">
                                              <Package className="w-3 h-3" />
                                              {offer.driver_completed_deliveries} Deliveries Completed
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 text-gray-600 text-[11px] font-medium rounded border border-gray-200">
                                              <Package className="w-3 h-3" />
                                              New Driver
                                            </span>
                                          )}
                                        </div>
                                        {offer.driver_bio && (
                                          <p className="text-xs text-gray-500 mt-1 italic line-clamp-2" data-testid={`offer-bio-${offer.id}`}>"{offer.driver_bio}"</p>
                                        )}
                                        {(offer.driver_payment_methods || offer.driver_accepts_direct) && (
                                          <div className="mt-2">
                                            <PaymentMethodBadges
                                              methods={offer.driver_payment_methods}
                                              dataTestId={`offer-payment-methods-${offer.id}`}
                                            />
                                          </div>
                                        )}
                                        {offer.driver_insurance_declared && (
                                          <div
                                            className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-800 text-[11px] font-medium rounded border border-indigo-200"
                                            data-testid={`offer-insurance-declared-${offer.id}`}
                                            title="Driver has declared they hold, and will maintain, the insurance required by law to carry out paid delivery work in Ireland."
                                          >
                                            <span aria-hidden="true">🛡️</span> Insurance Declared
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Vehicles — from the driver's bidding profile (up to two) */}
                                    {offer.driver_vehicle?.vehicles?.length > 0 && (
                                      <div className="mx-4 mb-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3" data-testid={`offer-vehicle-${offer.id}`}>
                                        <div className="flex items-start gap-2.5">
                                          <Truck className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                                          <div className="min-w-0 flex-1 space-y-2">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                              {offer.driver_vehicle.vehicles.length > 1 ? 'Vehicles available' : 'Vehicle'}
                                            </p>
                                            {offer.driver_vehicle.vehicles.map((v, vi) => (
                                              <div key={`${v.type}-${vi}`} className={vi > 0 ? 'pt-2 border-t border-dashed border-slate-200' : ''}>
                                                <div className="flex flex-wrap gap-1.5">
                                                  <span className="inline-block px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-semibold text-slate-700">{v.type}</span>
                                                  {v.tail_lift && (
                                                    <span className="inline-block px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-800">Tail lift</span>
                                                  )}
                                                </div>
                                                {(v.make_model || v.load_capacity) && (
                                                  <p className="text-xs text-slate-600 mt-1 break-words">
                                                    {v.make_model}
                                                    {v.make_model && v.load_capacity && ' · '}
                                                    {v.load_capacity && `Capacity: ${v.load_capacity}`}
                                                  </p>
                                                )}
                                                {v.notes && <p className="text-xs text-slate-500 mt-0.5 break-words">{v.notes}</p>}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Message — only render when present to keep the card compact */}
                                    {offer.message && (
                                      <p className="mx-4 mb-3 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded italic">"{offer.message}"</p>
                                    )}

                                    {/* Ask a Question — auto-expanded if driver has sent any
                                        replies (so the customer doesn't miss them). */}
                                    <div className="px-4 pb-3" data-testid={`ask-question-wrapper-${offer.id}`}>
                                      <AskAQuestion
                                        offerId={offer.id}
                                        role="customer"
                                        initialBid={offer.message || ''}
                                        defaultOpen={(offer.thread_message_count || 0) > 0 || !!offer.message}
                                      />
                                    </div>

                                    {/* Pricing — what you pay JustDeliver now vs the driver directly */}
                                    <div
                                      className="px-4 py-3 border-t border-gray-100 bg-gray-50/40"
                                      data-testid={`offer-price-${offer.id}`}
                                    >
                                      <div className="flex items-end justify-between gap-3">
                                        <span className="text-sm font-semibold text-gray-900">Total delivery price</span>
                                        <span
                                          className="text-2xl font-extrabold text-[#1B5E20] leading-none tabular-nums"
                                          data-testid={`offer-total-${offer.id}`}
                                        >
                                          €{pricing.total.toFixed(2)}
                                        </span>
                                      </div>
                                      <p
                                        className="text-[11px] text-gray-500 mt-1.5 sm:text-right"
                                        data-testid={`offer-fee-note-${offer.id}`}
                                      >
                                        Includes {feeLabel(pricing.bookingFee)} JustDeliver.ie booking fee
                                      </p>
                                    </div>

                                    {/* Accept & Pay CTA — Pay Driver Directly */}
                                    <div className="px-4 pb-4 pt-3">
                                      <Button
                                        onClick={() => handleOfferCTA(offer)}
                                        className="w-full min-w-0 whitespace-normal bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-md min-h-12 py-2.5 text-sm sm:text-base font-bold gap-2 leading-snug shadow-sm hover:shadow-md transition-all active:scale-[0.99]"
                                        disabled={processingPayment}
                                        data-testid={`accept-pay-btn-${offer.id}`}
                                      >
                                        <CreditCard className="w-4 h-4 flex-shrink-0" />
                                        <span className="min-w-0">{processingPayment ? 'Processing…' : 'Accept Offer · Secure Your Booking'}</span>
                                      </Button>
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* === Secondary info: collapsible to keep above-the-fold clean === */}
                            <div className="mt-5 space-y-2" data-testid="select-driver-secondary-info">
                              <details className="group rounded-md border border-emerald-100 bg-emerald-50/40 overflow-hidden">
                                <summary className="flex items-center justify-between cursor-pointer px-3 py-2.5 text-sm font-semibold text-[#1B5E20] hover:bg-emerald-50">
                                  <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Trust &amp; Protection</span>
                                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                                </summary>
                                <div className="px-3 pb-3 pt-1 text-xs text-gray-700">
                                  <p className="leading-relaxed mb-2">
                                    Driver names and numbers are shared the moment your payment to JustDeliver goes through — this protects you from off-platform scams and keeps every booking covered by our cancellation refund protection.
                                  </p>
                                  <ul className="space-y-1">
                                    {[
                                      'Encrypted card payment via Stripe — your card stays safe',
                                      'Driver only contacts you after you accept',
                                      'Full refund of both JustDeliver charges if your driver cancels',
                                    ].map((t) => (
                                      <li key={t} className="flex items-start gap-1.5">
                                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 text-[#2E7D32] mt-0.5" strokeWidth={2.25} />
                                        <span>{t}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </details>

                              <details className="group rounded-md border border-gray-200 bg-gray-50/60 overflow-hidden">
                                <summary className="flex items-center justify-between cursor-pointer px-3 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-100">
                                  <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-500" /> How the booking fee works</span>
                                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                                </summary>
                                <div className="px-3 pb-3 pt-1 text-xs text-gray-600">
                                  <p className="leading-relaxed">The €10 JustDeliver.ie booking fee covers secure card payments, verified drivers, fraud prevention, customer support and cancellation refund protection. It is never taken out of your driver&apos;s amount.</p>
                                  <p className="mt-1.5 text-gray-500">A flat €10 on every booking — already included in the total delivery price you see. You pay it now to secure your booking, then pay the rest directly to your driver.</p>
                                </div>
                              </details>

                              <details className="group rounded-md border border-gray-200 bg-gray-50/60 overflow-hidden" data-testid="customer-vat-notice">
                                <summary className="flex items-center justify-between cursor-pointer px-3 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-100">
                                  <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-500" /> VAT &amp; Invoices</span>
                                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                                </summary>
                                <div className="px-3 pb-3 pt-1 text-xs text-gray-600">
                                  <p className="leading-relaxed"><span className="font-semibold text-gray-800">All prices shown are final.</span> If a driver is VAT registered, VAT is included in their price. If you require a VAT invoice, the driver can provide one on request.</p>
                                </div>
                              </details>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
          {/* Customer Delivery Pagination */}
          {totalCustPages > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-5">
              <button onClick={() => setCustDeliveryPage(p => Math.max(1, p - 1))} disabled={custDeliveryPage === 1} className="px-4 py-2 text-sm font-semibold rounded-full border border-slate-200 bg-white disabled:opacity-30 hover:bg-slate-50 transition-colors">Prev</button>
              {Array.from({ length: totalCustPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setCustDeliveryPage(p)} className={`w-9 h-9 text-sm font-bold rounded-full transition-colors ${p === custDeliveryPage ? 'bg-[#2E7D32] text-white shadow-sm' : 'border border-slate-200 bg-white hover:bg-slate-50'}`}>{p}</button>
              ))}
              <button onClick={() => setCustDeliveryPage(p => Math.min(totalCustPages, p + 1))} disabled={custDeliveryPage === totalCustPages} className="px-4 py-2 text-sm font-semibold rounded-full border border-slate-200 bg-white disabled:opacity-30 hover:bg-slate-50 transition-colors">Next</button>
            </div>
          )}
          </>
          );
          })()}
        </DeliveryTabs>
        </>)}

        {/* PR 2 — Payment method choice dialog (Stripe vs Direct) */}
        <PaymentMethodChoiceDialog
          open={!!paymentChoiceOffer}
          offer={paymentChoiceOffer}
          onCancel={closePaymentChoice}
          onChoose={submitPaymentChoice}
          processing={processingPayment}
        />

        {/* In-app Stripe payment form */}
        <StripeCheckoutDialog
          open={!!checkoutSecret}
          clientSecret={checkoutSecret}
          onClose={closeCheckout}
        />


        {/* Edit Job Dialog (only allowed while delivery is pending + no driver) */}
        <Dialog open={cancelDialogOpen} onOpenChange={(o) => { setCancelDialogOpen(o); if (!o) setCancelTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="cancel-job-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Inter']">Why are you cancelling?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <p className="text-sm text-gray-600">
                This will remove all driver bids for this delivery. Please choose a reason — drivers will see it in their notification.
              </p>
              <div className="flex flex-col gap-2">
                {CANCEL_REASONS.map((r) => (
                  <Button
                    key={r.key}
                    type="button"
                    variant="outline"
                    className="justify-start text-left h-auto py-3 hover:bg-red-50 hover:border-red-300"
                    disabled={cancellingId === cancelTarget?.id}
                    onClick={() => confirmCancelWithReason(r.key, r.label)}
                    data-testid={`cancel-reason-${r.key}`}
                  >
                    {cancellingId === cancelTarget?.id ? 'Cancelling…' : r.label}
                  </Button>
                ))}
              </div>
              <div className="pt-2 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setCancelDialogOpen(false); setCancelTarget(null); }}
                  disabled={cancellingId === cancelTarget?.id}
                  data-testid="cancel-job-dialog-close"
                >
                  Keep delivery
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={editDialogOpen} onOpenChange={(o) => { setEditDialogOpen(o); if (!o) { setEditingDelivery(null); setEditForm(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-job-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Inter']">Edit delivery request</DialogTitle>
            </DialogHeader>
            {editForm && (
              <div className="space-y-5">
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    Drivers who already bid will be notified that the job details changed.
                  </AlertDescription>
                </Alert>

                {/* Item description + type */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Item description</Label>
                  <Textarea
                    value={editForm.item_description}
                    onChange={(e) => setEditForm({ ...editForm, item_description: e.target.value.slice(0, DESCRIPTION_MAX) })}
                    rows={3}
                    maxLength={DESCRIPTION_MAX}
                    className={`rounded-sm ${validateDescription(editForm.item_description) ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                    data-testid="edit-item-description"
                  />
                  <div className="flex items-start justify-between gap-3">
                    <p
                      className={`text-[11px] flex-1 ${validateDescription(editForm.item_description) ? 'text-red-600' : 'text-gray-500'}`}
                      data-testid="edit-item-description-helper"
                    >
                      {validateDescription(editForm.item_description) || DESCRIPTION_HELPER_TEXT}
                    </p>
                    <span
                      className={`text-[11px] tabular-nums flex-shrink-0 ${editForm.item_description.length > DESCRIPTION_MAX || (editForm.item_description.length > 0 && editForm.item_description.length < DESCRIPTION_MIN) ? 'text-red-600 font-medium' : 'text-gray-400'}`}
                      data-testid="edit-item-description-counter"
                    >
                      {editForm.item_description.length} / {DESCRIPTION_MAX}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">Item type</Label>
                    <select
                      value={editForm.item_type}
                      onChange={(e) => setEditForm({ ...editForm, item_type: e.target.value })}
                      className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#2E7D32]"
                      data-testid="edit-item-type"
                    >
                      <option value="">Select…</option>
                      {ITEM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editForm.weight_kg}
                      onChange={(e) => setEditForm({ ...editForm, weight_kg: e.target.value })}
                      className="rounded-sm"
                      data-testid="edit-weight"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">Length (cm)</Label>
                    <Input type="number" step="0.1" value={editForm.dimensions_length} onChange={(e) => setEditForm({ ...editForm, dimensions_length: e.target.value })} className="rounded-sm" data-testid="edit-length" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">Width (cm)</Label>
                    <Input type="number" step="0.1" value={editForm.dimensions_width} onChange={(e) => setEditForm({ ...editForm, dimensions_width: e.target.value })} className="rounded-sm" data-testid="edit-width" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">Height (cm)</Label>
                    <Input type="number" step="0.1" value={editForm.dimensions_height} onChange={(e) => setEditForm({ ...editForm, dimensions_height: e.target.value })} className="rounded-sm" data-testid="edit-height" />
                  </div>
                </div>

                {/* Optional guide-price budget (editable while pending) */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Your Budget (€) <span className="text-[11px] text-gray-400 font-normal">(optional)</span></Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    value={editForm.customer_budget}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') { setEditForm({ ...editForm, customer_budget: '' }); return; }
                      const n = parseFloat(raw);
                      if (Number.isFinite(n) && n >= 0) setEditForm({ ...editForm, customer_budget: raw });
                    }}
                    placeholder="e.g. 85"
                    className="rounded-sm"
                    data-testid="edit-customer-budget"
                  />
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Guide price only — not a maximum. Drivers use this to price closer to your expectations. Leave blank to clear.
                  </p>
                </div>

                {/* Delivery timeframe */}
                <div className="space-y-2 p-3 border border-gray-200 rounded-sm bg-gray-50">
                  <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Delivery timeframe</Label>
                  <div className="flex flex-wrap gap-2" data-testid="edit-time-type">
                    {[
                      { v: 'before', l: 'Before' },
                      { v: 'range', l: 'Time range' },
                      { v: 'flexible', l: 'Flexible' },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setEditForm({ ...editForm, time_type: opt.v })}
                        className={`px-3 py-1.5 rounded-sm text-sm font-medium border ${editForm.time_type === opt.v ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-gray-700 border-gray-200'}`}
                        data-testid={`edit-time-type-${opt.v}`}
                      >{opt.l}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Date</Label>
                      <Input type="date" value={editForm.time_date} onChange={(e) => setEditForm({ ...editForm, time_date: e.target.value })} className="rounded-sm" data-testid="edit-time-date" />
                    </div>
                    {editForm.time_type === 'before' && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Before (HH:MM AM/PM)</Label>
                        <ClockTimePicker value={editForm.time_before} onChange={(v) => setEditForm({ ...editForm, time_before: v })} dataTestId="edit-time-before" />
                      </div>
                    )}
                  </div>
                  {editForm.time_type === 'range' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">From</Label>
                        <ClockTimePicker value={editForm.time_from} onChange={(v) => setEditForm({ ...editForm, time_from: v })} dataTestId="edit-time-from" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">To</Label>
                        <ClockTimePicker value={editForm.time_to} onChange={(v) => setEditForm({ ...editForm, time_to: v })} dataTestId="edit-time-to" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Lifting & loading help */}
                <div className="space-y-3 p-3 border border-gray-200 rounded-sm">
                  <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Lifting &amp; loading help</Label>
                  <HelpChoice
                    label="At pickup (collection)"
                    value={editForm.collection_help}
                    onChange={(v) => setEditForm({ ...editForm, collection_help: v })}
                    options={[
                      { value: 'available', emoji: '✅', text: 'Someone will help load' },
                      { value: 'none',      emoji: '❌', text: 'No help available' },
                    ]}
                    dataTestId="edit-collection-help"
                  />
                  <HelpChoice
                    label="At drop-off (delivery)"
                    value={editForm.delivery_help}
                    onChange={(v) => setEditForm({ ...editForm, delivery_help: v })}
                    options={[
                      { value: 'available', emoji: '✅', text: 'Someone will help unload' },
                      { value: 'none',      emoji: '❌', text: 'No help available' },
                    ]}
                    dataTestId="edit-delivery-help"
                  />
                  <HelpChoice
                    label="Driver assistance needed?"
                    value={editForm.driver_assistance}
                    onChange={(v) => setEditForm({ ...editForm, driver_assistance: v })}
                    options={[
                      { value: 'driver_only',     emoji: '🚚', text: 'Driver only' },
                      { value: 'helper_required', emoji: '👥', text: 'Driver must bring a helper' },
                    ]}
                    dataTestId="edit-driver-assistance"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                    className="flex-1 border-gray-300 rounded-sm"
                    data-testid="edit-cancel-btn"
                  >Cancel</Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={editSubmitting}
                    className="flex-1 bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm gap-2 disabled:opacity-60"
                    data-testid="edit-save-btn"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {editSubmitting ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Inter']">Rate Your Driver</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-center" data-testid="review-stars">
                <p className="text-sm text-gray-600 mb-2">How was your delivery experience?</p>
                <div className="flex justify-center">
                  <StarRating rating={reviewRating} onRate={setReviewRating} interactive size="w-8 h-8" />
                </div>
                <p className="text-sm font-medium mt-1">{['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][reviewRating]}</p>
              </div>
              <div>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Leave a comment (optional)"
                  rows={3}
                  className="w-full p-3 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#2E7D32] resize-none"
                  data-testid="review-comment-input"
                />
              </div>
              <Button onClick={handleSubmitReview} disabled={reviewSubmitting} className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-2" data-testid="review-submit-btn">
                <Star className="w-4 h-4" />
                {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        {/* Detail + Receipt dialog (history/recent/archive rows) */}
        <DeliveryDetailDialog
          delivery={detailDelivery}
          open={detailOpen}
          onOpenChange={(o) => { setDetailOpen(o); if (!o) setDetailDelivery(null); }}
        />

        {/* === Post-Delivery Success Modal ===
         * Replaces the bare toast after a successful POST /api/deliveries.
         * Green checkmark + branded copy + dual CTAs (View My Job /
         * Post Another Delivery). Mobile-first responsive — full-width on
         * phones, comfortable max-w-md on desktop. */}
        <Dialog
          open={!!successDelivery}
          onOpenChange={(o) => { if (!o) setSuccessDelivery(null); }}
        >
          <DialogContent
            className="max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
            data-testid="post-success-dialog"
          >
            <DialogHeader className="sr-only">
              <DialogTitle>Your delivery request is now live</DialogTitle>
            </DialogHeader>
            <div className="relative rounded-2xl bg-white shadow-2xl ring-1 ring-emerald-100 overflow-hidden">
              {/* Top accent strip — JustDeliver brand green */}
              <div className="h-1.5 w-full bg-gradient-to-r from-[#2E7D32] via-[#4CAF50] to-[#F57C00]" />

              {/* Soft confetti-ish radial backdrop */}
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-100/60 blur-3xl"
              />
              <div
                aria-hidden
                className="absolute -bottom-20 -left-16 w-48 h-48 rounded-full bg-orange-100/50 blur-3xl"
              />

              <div className="relative px-6 py-7 sm:px-8 sm:py-8">
                {/* Green success checkmark */}
                <div className="flex justify-center">
                  <div
                    className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#4CAF50] to-[#2E7D32] flex items-center justify-center shadow-lg shadow-emerald-200"
                    data-testid="post-success-icon"
                  >
                    <CheckCircle className="w-10 h-10 text-white" strokeWidth={2.5} />
                    {/* Subtle pulse ring */}
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-full ring-4 ring-[#2E7D32]/15 animate-ping"
                      style={{ animationDuration: '2.4s' }}
                    />
                  </div>
                </div>

                {/* Headline */}
                <h2
                  className="mt-5 text-center text-xl sm:text-2xl font-extrabold text-gray-900 leading-snug"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  data-testid="post-success-headline"
                >
                  <span aria-hidden>🎉</span> Your delivery request is now live!
                </h2>

                {/* Body copy */}
                <p
                  className="mt-3 text-center text-sm sm:text-base text-gray-600 leading-relaxed"
                  data-testid="post-success-subtext"
                >
                  Drivers in your area can now view your job and submit offers.
                </p>

                {/* Helpful nudge — friendly amber callout */}
                <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200/80 p-3.5 sm:p-4">
                  <p className="text-xs sm:text-sm text-amber-900 leading-relaxed">
                    <span className="font-semibold" aria-hidden>💡 </span>
                    Once you’ve received offers you’re happy with, please choose your preferred driver as soon as possible. A quick decision helps drivers plan their schedules, organise their routes, and avoid missing out on other work.
                  </p>
                </div>

                {/* Thank-you line */}
                <p
                  className="mt-4 text-center text-xs sm:text-sm text-gray-500"
                  data-testid="post-success-thanks"
                >
                  Thank you for supporting JustDeliver.ie. <span aria-hidden>🚚💚🤍🧡</span>
                </p>

                {/* CTAs */}
                <div className="mt-6 flex flex-col gap-2.5">
                  <Button
                    onClick={() => {
                      const d = successDelivery;
                      setSuccessDelivery(null);
                      if (d) {
                        // Take the customer to their active deliveries tab
                        // and open the *live* Offers dialog for the freshly
                        // posted job (NOT the receipt-style DetailDialog —
                        // a fresh delivery has no receipt yet, just status
                        // + offers coming in). The card with matching
                        // selectedDelivery.id renders the Offers dialog.
                        setActiveView('deliveries');
                        setSearchParams({ view: 'deliveries' });
                        viewOffers(d);
                      }
                    }}
                    className="w-full h-11 bg-[#2E7D32] text-white hover:bg-[#1B5E20] font-semibold rounded-xl shadow-sm shadow-emerald-200/50"
                    data-testid="post-success-view-job-btn"
                  >
                    View My Job
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSuccessDelivery(null);
                      // Re-open the post-delivery form for another submission
                      setShowForm(true);
                    }}
                    className="w-full h-11 border-2 border-[#2E7D32]/30 text-[#1B5E20] hover:bg-emerald-50 hover:border-[#2E7D32]/50 font-semibold rounded-xl"
                    data-testid="post-success-post-another-btn"
                  >
                    Post Another Delivery
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ─── Book with Confidence — trust banner ────────────────────────
            Sits at the BOTTOM of the page (under the Messages / My Deliveries
            content) so it reassures without pushing the real content down.
            Hidden on the Home and Post views, which have their own hero. */}
        <section
          className={`relative overflow-hidden rounded-2xl shadow-lg mt-6 bg-gradient-to-br from-[#16a34a] to-[#22c55e] text-white ${activeView === 'home' || activeView === 'post' || activeView === 'messages' ? 'hidden' : ''}`}
          data-testid="customer-trust-banner"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl"
          />
          <div className="relative p-5 sm:p-6">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" strokeWidth={2} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold font-['Inter'] tracking-tight">
                  Book with Confidence
                </h2>
                <p className="text-white/95 text-sm sm:text-[15px] leading-relaxed mt-1">
                  Compare bids from verified independent drivers and choose the one that suits you. You pay JustDeliver&apos;s charges securely by card and pay your driver directly — our support team is here if you ever need assistance.
                </p>
              </div>
            </div>

            <div className="mt-4 sm:mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              {[
                { Icon: Shield,      label: 'Secure Payments',    testid: 'trust-badge-secure-payments' },
                { Icon: Truck,       label: 'Choose Your Driver', testid: 'trust-badge-choose-driver' },
                { Icon: HelpCircle,  label: 'Customer Support',   testid: 'trust-badge-customer-support' },
              ].map(({ Icon, label, testid }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 backdrop-blur-sm border border-white/20"
                  data-testid={testid}
                >
                  <Icon className="w-4 h-4 text-white flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                  <span className="text-sm font-semibold text-white">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <DashboardFooter />
      <SupportChat />
    </div>
  );
}
